// =======================================================================
// Quantity Engine — the single authoritative location for deterministic,
// pure quantity-calculation functions used by the Takeoff/Measurement
// workflow (Wave 6). This is the "Volume Calculation Layer" referenced
// in the Phase 2 architecture: Canvas geometry + explicit engineering
// inputs flow through here, and ONLY the result of these functions is
// ever persisted as a measurement.calculated_value.
//
// Rules enforced by convention across this whole file:
//   - Every function here is pure: (inputs) => result, no I/O, no state,
//     no React, no Supabase calls.
//   - No hidden defaults for values that represent an engineering/user
//     decision (e.g. depth) — callers must supply them explicitly.
//   - This file is the ONLY place volume/quantity math may live. UI
//     components may call these functions to render a live preview, but
//     must never reimplement the formula independently.
//
// Future specialized modules (Rebar, Masonry, etc. — later Wave 6
// phases) will add their own pure calculate() functions to this same
// file, following the identical pattern.
// =======================================================================

/**
 * Volume = Area (from Canvas geometry) × explicit Depth/Height.
 * Depth must always be a real, explicitly-provided positive number —
 * this function does not accept a default and will throw rather than
 * silently substitute one, so a caller can never accidentally compute a
 * volume from an unset depth.
 *
 * @param {number} area - measured area in m² (from Canvas shoelaceArea)
 * @param {number} depth - explicit depth/height in m, entered by the user
 * @returns {number} volume in m³
 */
export function calculateVolume(area, depth) {
  if (typeof area !== 'number' || Number.isNaN(area) || area <= 0) {
    throw new Error('calculateVolume: area must be a positive number.')
  }
  if (typeof depth !== 'number' || Number.isNaN(depth) || depth <= 0) {
    throw new Error('calculateVolume: depth must be an explicit positive number — it cannot be inferred or defaulted.')
  }
  return area * depth
}

// =======================================================================
// Rebar Engineering Constants & Pure Calculation Functions.
//
// STEEL_DENSITY is the ONLY built-in physical constant permitted by the
// approved specification — it is not an engineering assumption, it is
// the physical density of structural steel (kg/m³), used purely to
// derive unit weight per running metre from a bar diameter via its
// cross-sectional area. Every other value (diameter, spacing, cover,
// lap%, waste%) is a mandatory, explicit engineering input with no
// hidden default anywhere in this file.
// =======================================================================
const STEEL_DENSITY_KG_PER_M3 = 7850

/**
 * Unit weight of a reinforcement bar per running metre, derived purely
 * from its diameter and the physical steel density constant. This is
 * the ONLY built-in physical constant — every other quantity in this
 * module is an explicit user/engineering input with no hidden default.
 */
// =======================================================================
// Breakdown Transparency Contract (LOCKED, GLOBAL RULE)
//
// Every renderBreakdown() across every module must expose EVERY input
// that materially affects the displayed quantity — not just derived
// results. `row()` is the shared builder used everywhere so every
// module's Breakdown has the same visual/structural shape, and adding
// a row is a one-line call rather than hand-rolled markup.
// =======================================================================
function row(label, value, opts = {}) {
  return { label, value, ...opts }
}

export function calculateUnitWeight(diameterMm) {
  if (typeof diameterMm !== 'number' || Number.isNaN(diameterMm) || diameterMm <= 0) {
    throw new Error('Bar diameter must be a positive number.')
  }
  const radiusM = (diameterMm / 1000) / 2
  const crossSectionAreaM2 = Math.PI * radiusM * radiusM
  return crossSectionAreaM2 * STEEL_DENSITY_KG_PER_M3
}

/**
 * Full reinforcement calculation for ONE layer on ONE slab, following the
 * approved Final Complete Rebar Contract exactly:
 *   Extent -> effective dimension -> bar count -> actual spacing ->
 *   opening deduction -> cutting length -> total length -> weight.
 *
 * Openings use Model A (Simple Quantity Deduction Only, LOCKED):
 *   - A bar's centreline position (computed from ACTUAL spacing, not the
 *     entered spacing) is tested against every opening's perpendicular
 *     range.
 *   - Any bar intersecting ANY opening is deducted once — a single
 *     `.some()` test per bar prevents double-deduction across
 *     overlapping openings.
 *   - No interrupted-bar reconstruction and no automatic trimming/edge
 *     reinforcement are performed — that remains the estimator's
 *     responsibility via a separate explicit Layer.
 *
 * @param {number} parallelDim - dimension the bar runs ALONG (m) — from
 *   either the full slab or the layer's localExtent.barLengthDim
 * @param {number} perpendicularDim - dimension bars are DISTRIBUTED
 *   ACROSS (m) — from the full slab or localExtent.distributionWidthDim
 * @param {object} layer - { label, diameter (mm), spacing (mm), cover (mm),
 *   hookAllowance (mm, default 0), developmentLengthOverride (mm, default 0) }
 * @param {Array<{offsetAlongPerpendicular:number, extentAlongPerpendicular:number}>} openings - in m
 */
export function calculateSlabLayerForSlab(parallelDim, perpendicularDim, layer, openings = []) {
  const { diameter, spacing, cover } = layer
  const hookAllowanceM = (layer.hookAllowance || 0) / 1000
  const developmentLengthOverrideM = (layer.developmentLengthOverride || 0) / 1000

  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) {
    throw new Error(`Layer "${layer.label}": bar diameter is required.`)
  }
  if (typeof spacing !== 'number' || Number.isNaN(spacing) || spacing <= 0) {
    throw new Error(`Layer "${layer.label}": spacing must be a positive number.`)
  }
  if (typeof cover !== 'number' || Number.isNaN(cover) || cover < 0) {
    throw new Error(`Layer "${layer.label}": cover is required.`)
  }

  const coverM = cover / 1000
  const spacingM = spacing / 1000

  const effectiveDimension = perpendicularDim - 2 * coverM
  if (effectiveDimension <= 0) {
    throw new Error(`Layer "${layer.label}": effective distribution dimension is ≤ 0 — cover is too large relative to the slab/extent dimension.`)
  }

  const rawCuttingLength = parallelDim - 2 * coverM
  if (rawCuttingLength <= 0) {
    throw new Error(`Layer "${layer.label}": bar cutting length is ≤ 0 — cover is too large relative to the slab/extent dimension.`)
  }

  const numberOfSpaces = Math.ceil(effectiveDimension / spacingM)
  const numberOfBars = numberOfSpaces + 1
  // Actual spacing is the true centre-to-centre distance once the bar
  // count has been rounded up — shown transparently to the estimator,
  // and used (not the entered `spacing`) for every bar-position test.
  const actualSpacing = effectiveDimension / numberOfSpaces

  // --- Opening deduction (Model A) ---
  let deductedBars = 0
  if (openings.length > 0) {
    for (let i = 0; i < numberOfBars; i++) {
      const barPosition = coverM + i * actualSpacing
      const intersects = openings.some(
        (op) => barPosition >= op.offsetAlongPerpendicular && barPosition <= (op.offsetAlongPerpendicular + op.extentAlongPerpendicular)
      )
      if (intersects) deductedBars++
    }
  }
  const finalNumberOfBars = numberOfBars - deductedBars

  const cuttingLength = rawCuttingLength + hookAllowanceM + developmentLengthOverrideM
  const totalLength = finalNumberOfBars * cuttingLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight

  return {
    effectiveDimension, numberOfSpaces, numberOfBars, actualSpacing,
    deductedBars, finalNumberOfBars, cuttingLength, totalLength, unitWeight, grossWeight,
  }
}

/**
 * Full Rebar Slab calculation across multiple independently-dimensioned
 * slabs and dynamic reinforcement layers. Each slab is calculated fully
 * independently — slabs are NEVER combined by summing areas before
 * calculating bar counts (LOCKED).
 *
 * Produces three distinct, professionally-defined quantities per the
 * LOCKED contract — the caller (UI) presents all three and the estimator
 * explicitly picks which one becomes a given Output's calculated_value:
 *   theoretical        - Σ(bars × cutting length × unit weight), no allowance
 *   theoreticalPlusLap - theoretical × (1 + lapPct%)
 *   procurement        - (theoretical + lap) × (1 + wastePct%)
 *
 * @param {Array<{label, length, width, openings?}>} slabs
 * @param {Array<object>} layers - each with direction, diameter, spacing,
 *   coverPosition, extentType, localExtent?, hookAllowance?, developmentLengthOverride?
 * @param {{top:number, bottom:number}} covers - mm
 * @param {{lapPct:number, wastePct:number}} allowances - percentages
 */
export function calculateRebarSlab(slabs, layers, covers, allowances) {
  if (!Array.isArray(slabs) || slabs.length === 0) {
    throw new Error('At least one slab geometry must be selected.')
  }
  if (!Array.isArray(layers) || layers.length === 0) {
    throw new Error('At least one reinforcement layer must be defined.')
  }
  if (typeof allowances?.lapPct !== 'number' || Number.isNaN(allowances.lapPct)) {
    throw new Error('Lap/Splice Allowance % is required.')
  }
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) {
    throw new Error('Waste Allowance % is required.')
  }

  const slabBreakdown = []
  let theoretical = 0

  for (const slab of slabs) {
    if (typeof slab.length !== 'number' || slab.length <= 0 || typeof slab.width !== 'number' || slab.width <= 0) {
      throw new Error(`Slab "${slab.label}": could not recover valid Length/Width from its geometry.`)
    }
    const layerResults = []
    let slabGross = 0
    for (const layer of layers) {
      const cover = layer.coverPosition === 'top' ? covers.top : covers.bottom

      // Extent: 'local' uses the layer's own barLengthDim/distributionWidthDim
      // (independent of the slab's own dimensions); 'full_slab' derives the
      // two dimensions from the slab itself according to bar Direction.
      let parallelDim, perpendicularDim
      if (layer.extentType === 'local') {
        if (!layer.localExtent || typeof layer.localExtent.barLengthDim !== 'number' || layer.localExtent.barLengthDim <= 0
          || typeof layer.localExtent.distributionWidthDim !== 'number' || layer.localExtent.distributionWidthDim <= 0) {
          throw new Error(`Layer "${layer.label}": local extent requires a positive Bar Length and Distribution Width.`)
        }
        parallelDim = layer.localExtent.barLengthDim
        perpendicularDim = layer.localExtent.distributionWidthDim
      } else {
        parallelDim = layer.direction === 'X' ? slab.length : slab.width
        perpendicularDim = layer.direction === 'X' ? slab.width : slab.length
      }

      // Openings only apply to full_slab layers, expressed relative to
      // the slab itself (Model A, LOCKED) — for 'local' extents the
      // estimator would size the localExtent to already exclude any
      // opening if relevant.
      const openings = layer.extentType === 'full_slab' ? (slab.openings || []) : []

      const calc = calculateSlabLayerForSlab(parallelDim, perpendicularDim, { ...layer, cover }, openings)
      layerResults.push({ layer, cover, ...calc })
      slabGross += calc.grossWeight
    }
    slabBreakdown.push({ slab, layerResults, slabGross })
    theoretical += slabGross
  }

  const lapAllowance = theoretical * (allowances.lapPct / 100)
  const theoreticalPlusLap = theoretical + lapAllowance
  const wasteAllowance = theoreticalPlusLap * (allowances.wastePct / 100)
  const procurement = theoreticalPlusLap + wasteAllowance

  const steps = [
    { label: 'Theoretical Steel Weight', value: theoretical, unit: 'kg' },
    { label: `Lap/Splice Allowance (${allowances.lapPct}%, simplified estimating allowance)`, value: lapAllowance, unit: 'kg' },
    { label: 'Theoretical + Lap', value: theoreticalPlusLap, unit: 'kg' },
    { label: `Waste Allowance (${allowances.wastePct}%, manufacturing/cutting waste only)`, value: wasteAllowance, unit: 'kg' },
    { label: 'Procurement Weight', value: procurement, unit: 'kg' },
  ]

  return {
    slabBreakdown,
    steps,
    quantityBases: {
      theoretical: { label: 'Theoretical Steel Weight', valueKg: theoretical, valueTon: theoretical / 1000 },
      theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLap, valueTon: theoreticalPlusLap / 1000 },
      procurement: { label: 'Procurement Weight', valueKg: procurement, valueTon: procurement / 1000 },
    },
    finalUnit: 'ton',
  }
}
//
// A ModuleDefinition describes one specialized engineering quantity
// module. Every module, present or future (Rebar Slab, Masonry, Sheet
// Metal, etc.), follows this exact same shape — the ONLY thing that
// differs between modules is the data below, never the surrounding
// mechanism. SpecializedModulePanel.jsx is the single generic component
// that renders any ModuleDefinition; it is never duplicated per module.
//
// ModuleDefinition shape:
//   moduleKey        - unique string, persisted as quantity_source.module_key
//   label            - display name in the module picker
//   geometrySource   - 'length' | 'area' | 'count' | 'none'
//                       ('none' = no takeoff_item needed, e.g. pure manual input)
//   inputFields      - [{ key, label, unit?, type:'number', required:true }]
//                       every field here is an explicit engineering/user
//                       input — no hidden defaults are ever supplied by
//                       the framework itself.
//   outputUnit       - unit label shown next to the final quantity
//   calculate(geometry, inputs) -> { steps: [{label, value, unit}], finalQuantity, finalUnit }
//                       PURE function: no I/O, no React, deterministic.
//
// This is intentionally the ONLY module registered in Phase 3. It exists
// solely to prove the framework end-to-end and must not be treated as a
// real engineering feature.
// =======================================================================

export const MODULE_REGISTRY = {
  test_linear_quantity: {
    moduleKey: 'test_linear_quantity',
    label: 'Test — Linear × Factor (framework validation only)',
    geometrySource: 'length',
    inputFields: [
      { key: 'factor', label: 'Factor', unit: '', type: 'number', required: true },
    ],
    outputUnit: '',
    explanation: {
      method: 'Final Quantity = Geometry Length × Factor. A single multiplication of the measured length by an estimator-entered factor.',
      assumptions: ['This is a proof-of-concept/test module — no real engineering assumptions apply.'],
      inputSources: [
        { label: 'Length', source: 'Takeoff Geometry (Canvas measurement)' },
        { label: 'Factor', source: 'Estimator input' },
      ],
    },
    calculate(geometry, inputs) {
      const length = geometry?.aggregatedQuantity
      const factor = Number(inputs.factor)
      if (typeof length !== 'number' || Number.isNaN(length) || length <= 0) {
        throw new Error('A valid Length takeoff item must be selected.')
      }
      if (typeof factor !== 'number' || Number.isNaN(factor) || inputs.factor === '' || inputs.factor == null) {
        throw new Error('Factor is required.')
      }
      const finalQuantity = length * factor
      return {
        steps: [
          { label: 'Length (from Canvas)', value: length, unit: 'm' },
          { label: 'Factor (input)', value: factor, unit: '' },
        ],
        finalQuantity,
        finalUnit: '',
      }
    },
  },

  rebar_slab: {
    moduleKey: 'rebar_slab',
    family: 'rebar',
    label: 'Rebar — Slab',
    discipline: 'structural',
    // NOTE: this entry exists ONLY as a registry marker so 'Rebar — Slab'
    // appears in module-picker lists that enumerate MODULE_REGISTRY keys.
    // Its actual UI is the dedicated RebarSlabModulePanel component
    // (rendered directly by SetQuantityModal / QuantificationPage when
    // this key is selected) — never SpecializedModulePanel's generic
    // single-geometry renderer. Its calculation logic lives exclusively
    // in calculateRebarSlab(); this entry does not implement calculate()
    // itself to guarantee there is only ONE Rebar Slab implementation.
    geometrySource: 'rectangle_multi',
    outputUnit: 'ton',
    isDedicatedPanel: true,
    explanation: {
      method: 'For each Layer on each Slab: Number of Bars = ceil(Effective Width ÷ Spacing) + 1 (so bars start and end exactly at the slab/extent edges). '
        + 'Cutting Length = (Bar Length dimension − 2×Cover) + optional Hook Allowance + optional Development Length Override. '
        + 'Bar Weight = Number of Bars × Cutting Length × Unit Weight, summed across every Layer and every Slab (each Slab calculated independently — areas are never pre-summed). '
        + 'Three quantities are produced: Theoretical Steel Weight (no allowance), Theoretical + Lap (× (1+Lap%)), and Procurement Weight (× (1+Waste%) on top of that). '
        + 'You choose which of the three becomes the saved quantity.',
      assumptions: [
        'Lap/Splice % is a simplified estimating allowance, not an actual stock-bar splice calculation.',
        'Waste % represents general manufacturing/cutting waste only — it is not a cutting-optimization mechanism.',
        'Openings deduct any bar whose centreline position falls inside the opening — this removes the WHOLE bar, not a partial segment, and does NOT add trimming/edge reinforcement automatically.',
        'Hook/Bend and Development Length are optional manual overrides only — the module never designs or calculates required development length automatically; that remains an engineering decision entered explicitly by the estimator.',
        'Only rectangular slabs and rectangular openings/local zones are supported — irregular (non-rectangular) slab geometry is not supported.',
      ],
      inputSources: [
        { label: 'Slab Length/Width', source: 'Takeoff Geometry (Canvas Rectangle measurement + calibration)' },
        { label: 'Bar Diameter, Spacing, Direction, Cover, Extent, Openings, Hook/Development overrides', source: 'Estimator input' },
        { label: 'Lap % and Waste %', source: 'Estimator input (simplified allowances)' },
        { label: 'Steel Unit Weight', source: 'Physical constant: π × (diameter/1000)² ÷ 4 × 7850 kg/m³ steel density' },
      ],
      glossary: [
        { term: 'Cover', definition: 'The concrete thickness protecting the reinforcement from the nearest surface.' },
        { term: 'Effective Dimension', definition: 'The usable width across which bars are distributed, after deducting cover from both edges.' },
        { term: 'Actual Spacing', definition: 'The real centre-to-centre distance once the bar count is rounded up — may differ slightly from the spacing you entered.' },
        { term: 'Hook Allowance', definition: 'Extra length added at a bar end for a bent/hooked anchorage, increasing grip in the concrete.' },
        { term: 'Development Length Override', definition: 'A manually-entered extra length ensuring a bar is properly anchored — this module never designs this value automatically; it must come from the structural engineer/code.' },
        { term: 'Lap/Splice %', definition: 'A simplified allowance representing extra steel needed where two bars overlap to form a continuous length.' },
        { term: 'Waste %', definition: 'General manufacturing/cutting waste only — not related to lap or stock-bar optimization.' },
        { term: 'Theoretical / Procurement', definition: 'Theoretical = steel physically required with no allowances. Procurement = Theoretical + Lap + Waste, i.e. what you would actually purchase.' },
        { term: 'Openings (Model A)', definition: 'Any bar crossing an opening is removed entirely. No trimming/edge reinforcement is added automatically — add it as a separate Layer if required.' },
        { term: 'Local Extent', definition: 'A reinforcement zone smaller than the full slab (e.g. extra bars over a beam) — defined by its own bar length and distribution width, independent of the slab\'s overall dimensions.' },
      ],
    },
    // Generic Details-view contract: any module MAY provide
    // renderBreakdown(engineeringInputs, breakdownSnapshot) returning a
    // plain, React-agnostic description of what to show in the
    // estimator-facing Details panel — built entirely from data already
    // stored in breakdown_snapshot/engineering_inputs, with NO
    // recalculation. Modules without this function fall back to the
    // Generic UI's existing raw-JSON view; nothing here is hard-coded
    // into QuantificationPage.jsx itself.
    renderBreakdown(engineeringInputs, breakdownSnapshot) {
      const sections = (breakdownSnapshot?.slabBreakdown || []).map((sb) => ({
        title: `${sb.slab.label} — ${sb.slab.length?.toFixed?.(2) ?? sb.slab.length} × ${sb.slab.width?.toFixed?.(2) ?? sb.slab.width} m`,
        // Openings are shown INSIDE this specific slab's own section — each
        // opening is unambiguously traceable to the exact slab it was
        // drawn against, never mixed into a global cross-slab list.
        rows: (sb.slab.openings || []).length > 0
          ? (sb.slab.openings || []).map((o, oi) => row(`Opening ${oi + 1} (Offset ${o.offsetAlongPerpendicular}m, Extent ${o.extentAlongPerpendicular}m)`, 'Model A — intersecting bars deducted'))
          : [],
        subsections: sb.layerResults.map((lr) => ({
          title: `${lr.layer.label || 'Layer'} (${lr.layer.extentType === 'local' ? 'Local Extent' : 'Full Slab'}, Direction ${lr.layer.direction}, Ø${lr.layer.diameter}mm)`,
          rows: [
            row('Input Spacing', `${lr.layer.spacing} mm`),
            row('Cover Position / Cover', `${lr.layer.coverPosition} / ${lr.cover} mm`),
            ...(lr.layer.extentType === 'local' ? [row('Local Extent (Bar Length × Distribution Width)', `${lr.layer.localExtent?.barLengthDim} × ${lr.layer.localExtent?.distributionWidthDim} m`)] : []),
            ...(lr.layer.hookAllowance > 0 ? [row('Hook Allowance', `${lr.layer.hookAllowance} mm`)] : []),
            ...(lr.layer.developmentLengthOverride > 0 ? [row('Development Length Override', `${lr.layer.developmentLengthOverride} mm`)] : []),
            row('Effective Dimension', `${lr.effectiveDimension?.toFixed?.(3)} m`),
            row('Number of Bars', lr.numberOfBars),
            ...(lr.deductedBars > 0 ? [row('Deducted (Openings, this layer)', `${lr.deductedBars} → Final Bars: ${lr.finalNumberOfBars}`)] : []),
            row('Actual Spacing', `${(lr.actualSpacing * 1000)?.toFixed?.(2)} mm`),
            row('Cutting Length', `${lr.cuttingLength?.toFixed?.(3)} m`),
            row('Total Length', `${lr.totalLength?.toFixed?.(2)} m`),
            row('Layer Weight', `${lr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
          ],
        })),
        footer: { label: 'Slab Total Weight', value: `${sb.slabGross?.toFixed?.(2)} kg` },
      }))

      const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))

      const basisLabels = { theoretical: 'Theoretical Steel Weight', theoreticalPlusLap: 'Theoretical + Lap', procurement: 'Procurement Weight' }
      const chosenBasis = engineeringInputs?.quantityBasisChosen
      const chosen = breakdownSnapshot?.quantityBases?.[chosenBasis]

      return {
        sections,
        summary: {
          title: 'Calculation Summary',
          rows: summaryRows,
        },
        selectedBasis: chosen ? { label: `Selected Quantity Basis: ${basisLabels[chosenBasis] || chosenBasis}`, value: `${chosen.valueTon?.toFixed?.(3)} ton` } : null,
      }
    },
  },

  rebar_beam: {
    moduleKey: 'rebar_beam',
    family: 'rebar',
    label: 'Rebar — Beam',
    discipline: 'structural',
    // Same principle as rebar_slab: this entry is a registry marker only
    // (so 'Rebar — Beam' appears in module-picker lists). The actual UI
    // is the dedicated RebarBeamModulePanel component, and the sole
    // calculation source is calculateRebarBeam() — never duplicated.
    geometrySource: 'length_multi',
    outputUnit: 'ton',
    isDedicatedPanel: true,
    explanation: {
      method: 'Longitudinal Reinforcement: each Group has an explicit bar count (never derived from spacing). Bar Length = Beam Length − 2×Cover + optional Hook/Development overrides, OR an explicit length if the estimator specifies one directly (e.g. a curtailed bar). '
        + 'Stirrups: each Zone spans an explicit start/end offset along the beam. Number of Stirrups = ceil(Zone Length ÷ Spacing), plus 1 extra only for the very first zone (so the shared boundary stirrup between adjacent zones is never counted twice). '
        + 'Stirrup Cutting Length = the outer 2-leg perimeter (2×internal height + 2×internal width + hooks) plus one additional internal cross-tie length for every extra pair of legs beyond 2 (a 4-leg stirrup is fabricated as an outer stirrup plus a separate, shorter internal tie — not a doubled-width single loop). '
        + 'Three quantities are produced: Theoretical, Theoretical + Lap, and Procurement — you choose which becomes the saved quantity, exactly as with Rebar Slab.',
      assumptions: [
        'Stirrup zones must cover the full beam length with zero gaps and zero overlaps — this is strictly enforced and blocks the calculation if violated, because any gap is a genuine under-count of required stirrups.',
        'Longitudinal bar counts are always an explicit estimator input per group — the module never derives how many bars are needed from beam dimensions.',
        'Curtailment is represented by creating a separate Longitudinal Group with an explicit bar length — the module never automatically shortens or redesigns bar curtailment.',
        'Development Length and Hook Allowance are optional manual overrides only — never automatically designed.',
        'Lap/Splice % is a simplified estimating allowance, not an actual stock-bar splice calculation.',
        'Waste % represents general manufacturing/cutting waste only.',
        'Beam openings/embedments are not automatically compensated — any additional reinforcement around them must be entered explicitly as its own Longitudinal Group or Stirrup Zone.',
      ],
      inputSources: [
        { label: 'Beam Length', source: 'Takeoff Geometry (Canvas Length measurement)' },
        { label: 'Beam Width / Overall Depth', source: 'Estimator input (section dimensions are not measurable from a plan-view drawing)' },
        { label: 'Covers, bar counts, diameters, spacing, zone offsets, hooks', source: 'Estimator input' },
        { label: 'Lap % and Waste %', source: 'Estimator input (simplified allowances)' },
        { label: 'Steel Unit Weight', source: 'Physical constant: π × (diameter/1000)² ÷ 4 × 7850 kg/m³ steel density' },
      ],
      glossary: [
        { term: 'Longitudinal Group', definition: 'A set of bars sharing the same position, diameter and count (e.g. "4Ø20 Bottom Main") — bar count is always entered directly, never derived from spacing.' },
        { term: 'Position (Top/Bottom/Side)', definition: 'Where the bar group sits in the beam cross-section — each uses its matching cover (Top, Bottom, or Side).' },
        { term: 'Explicit Bar Length', definition: 'A manually-entered bar length that replaces the automatic calculation — used for a curtailed bar that doesn\'t run the full beam length.' },
        { term: 'Development Length Override', definition: 'A manually-entered extra length ensuring a bar is properly anchored — never calculated automatically; it must come from the structural engineer/code.' },
        { term: 'Stirrup Zone', definition: 'A section of the beam (defined by a start and end position) with its own stirrup diameter and spacing — beams often need closer stirrups near supports than at midspan.' },
        { term: 'Legs (2-leg / 4-leg)', definition: 'How many vertical stirrup legs cross the section. A 4-leg stirrup is fabricated as one outer stirrup plus a separate internal cross-tie, not one wider loop.' },
        { term: 'Actual Spacing', definition: 'The real centre-to-centre stirrup spacing once the count is rounded — may differ slightly from the spacing you entered.' },
        { term: 'Lap/Splice %', definition: 'A simplified allowance representing extra steel needed where two bars overlap to form a continuous length.' },
        { term: 'Waste %', definition: 'General manufacturing/cutting waste only — not related to lap or stock-bar optimization.' },
        { term: 'Theoretical / Procurement', definition: 'Theoretical = steel physically required with no allowances. Procurement = Theoretical + Lap + Waste, i.e. what you would actually purchase.' },
      ],
    },
    renderBreakdown(engineeringInputs, breakdownSnapshot) {
      const covers = engineeringInputs?.covers || {}
      const sections = (breakdownSnapshot?.beamBreakdown || []).map((bb) => ({
        title: `${bb.beam.label} — ${bb.beam.length?.toFixed?.(2)} m long, ${bb.beam.width?.toFixed?.(2)}×${bb.beam.overallDepth?.toFixed?.(2)} m section`,
        rows: [
          row('Covers (Top / Bottom / Side)', `${covers.top ?? '—'} / ${covers.bottom ?? '—'} / ${covers.side ?? '—'} mm`),
        ],
        subsections: [
          ...bb.longitudinalResults.map((lr) => ({
            title: `${lr.group.label || 'Longitudinal Group'} (${lr.group.position}, Ø${lr.group.diameter}mm × ${lr.group.numberOfBars})`,
            rows: [
              ...(lr.group.explicitBarLength > 0 ? [row('Explicit Bar Length (overrides derived length)', `${lr.group.explicitBarLength} m`)] : []),
              ...(lr.group.hookAllowance > 0 ? [row('Hook Allowance', `${lr.group.hookAllowance} mm`)] : []),
              ...(lr.group.developmentLengthOverride > 0 ? [row('Development Length Override', `${lr.group.developmentLengthOverride} mm`)] : []),
              row('Bar Length', `${lr.barLength?.toFixed?.(3)} m`),
              row('Total Length', `${lr.totalLength?.toFixed?.(2)} m`),
              row('Group Weight', `${lr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
            ],
          })),
          ...bb.stirrupResults.map((sr) => ({
            title: `${sr.zone.label || 'Stirrup Zone'} (Ø${sr.zone.diameter}mm @ ${sr.zone.spacing}mm, ${sr.zone.legs}-leg)`,
            rows: [
              row('Zone Extent (Start → End)', `${sr.zone.startOffset}m → ${sr.zone.endOffset}m`),
              row('Hooks per Stirrup / Hook Length', `${sr.zone.numberOfHooksPerStirrup} × ${sr.zone.hookLengthPerHook}mm`),
              row('Number of Stirrups', sr.numberOfStirrups),
              row('Actual Spacing', `${(sr.actualSpacing * 1000)?.toFixed?.(2)} mm`),
              row('Cutting Length', `${sr.cuttingLength?.toFixed?.(3)} m`),
              row('Zone Weight', `${sr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
            ],
          })),
        ],
        footer: { label: 'Beam Total Weight', value: `${bb.beamGross?.toFixed?.(2)} kg` },
      }))

      const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
      const basisLabels = { theoretical: 'Theoretical Steel Weight', theoreticalPlusLap: 'Theoretical + Lap', procurement: 'Procurement Weight' }
      const chosenBasis = engineeringInputs?.quantityBasisChosen
      const chosen = breakdownSnapshot?.quantityBases?.[chosenBasis]

      return {
        sections,
        summary: { title: 'Calculation Summary', rows: summaryRows },
        selectedBasis: chosen ? { label: `Selected Quantity Basis: ${basisLabels[chosenBasis] || chosenBasis}`, value: `${chosen.valueTon?.toFixed?.(3)} ton` } : null,
      }
    },
  },
}

// =======================================================================
// Rebar Beam — Pure Calculation Functions (LOCKED Final Contract)
//
// Architecturally distinct from Rebar Slab per the approved engineering
// review: beams use Longitudinal Groups (explicit bar counts, not
// spacing-derived) plus Stirrup Zones (spacing-derived, with mandatory
// full-length coverage validation) — Slab's Layer/extentType/localExtent
// model does NOT apply here and is intentionally not reused.
// =======================================================================

/**
 * ONE longitudinal reinforcement group (e.g. "Bottom Main", "Top Extra",
 * "Side Bars"). Bar count is always an explicit estimator input — never
 * derived from spacing, unlike Slab layers.
 */
export function calculateLongitudinalGroup(beam, group) {
  const { diameter, numberOfBars, position } = group
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) {
    throw new Error(`Longitudinal group "${group.label}": bar diameter is required.`)
  }
  if (typeof numberOfBars !== 'number' || Number.isNaN(numberOfBars) || numberOfBars <= 0) {
    throw new Error(`Longitudinal group "${group.label}": number of bars must be a positive number.`)
  }
  const cover = position === 'top' ? beam.topCover : position === 'bottom' ? beam.bottomCover : beam.sideCover
  if (typeof cover !== 'number' || Number.isNaN(cover) || cover < 0) {
    throw new Error(`Longitudinal group "${group.label}": the relevant cover (${position}) is required.`)
  }
  const coverM = cover / 1000
  const hookAllowanceM = (group.hookAllowance || 0) / 1000
  const developmentLengthOverrideM = (group.developmentLengthOverride || 0) / 1000

  let barLength
  if (typeof group.explicitBarLength === 'number' && group.explicitBarLength > 0) {
    // The estimator explicitly specifies the bar length (e.g. a
    // curtailed bar) — this REPLACES the derived length entirely; no
    // automatic curtailment logic is performed by the module itself.
    if (group.explicitBarLength > beam.length) {
      throw new Error(`Longitudinal group "${group.label}": explicit bar length exceeds beam length.`)
    }
    barLength = group.explicitBarLength
  } else {
    barLength = beam.length - 2 * coverM + hookAllowanceM + developmentLengthOverrideM
    if (barLength <= 0) {
      throw new Error(`Longitudinal group "${group.label}": derived bar length is ≤ 0 — cover is too large relative to beam length.`)
    }
  }

  const totalLength = numberOfBars * barLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight

  return { cover, barLength, totalLength, unitWeight, grossWeight }
}

/**
 * ONE stirrup zone. Cutting length is derived from the OUTER stirrup
 * (always present, 2-leg equivalent perimeter) plus additional internal
 * cross-ties for legs beyond 2 — this reflects the real fabricated
 * configuration (a 4-leg stirrup is an outer stirrup + a separate,
 * shorter internal cross-tie), NOT a naive width multiplication.
 *
 * Bar-count boundary handling: the FIRST zone counts its starting
 * stirrup (+1); every subsequent zone does NOT (+0), because its
 * starting position is the same physical stirrup already counted as
 * the previous zone's last one — this guarantees zero double-counting
 * at any shared zone boundary while using ACTUAL (not nominal) spacing
 * for every bar-position calculation.
 */
export function calculateStirrupZone(beam, zone, isFirstZone) {
  const { diameter, spacing, legs, numberOfHooksPerStirrup, hookLengthPerHook, startOffset, endOffset } = zone
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) {
    throw new Error(`Stirrup zone "${zone.label}": diameter is required.`)
  }
  if (typeof spacing !== 'number' || Number.isNaN(spacing) || spacing <= 0) {
    throw new Error(`Stirrup zone "${zone.label}": spacing must be a positive number.`)
  }
  if (legs !== 2 && legs !== 4) {
    throw new Error(`Stirrup zone "${zone.label}": legs must be 2 or 4.`)
  }
  if (typeof numberOfHooksPerStirrup !== 'number' || numberOfHooksPerStirrup <= 0) {
    throw new Error(`Stirrup zone "${zone.label}": number of hooks per stirrup is required.`)
  }
  if (typeof hookLengthPerHook !== 'number' || Number.isNaN(hookLengthPerHook) || hookLengthPerHook <= 0) {
    throw new Error(`Stirrup zone "${zone.label}": hook length per hook is required (stirrups always require hooks).`)
  }
  const zoneLength = endOffset - startOffset
  if (zoneLength <= 0) {
    throw new Error(`Stirrup zone "${zone.label}": end offset must be greater than start offset.`)
  }

  const internalHeight = beam.overallDepth - (beam.topCover / 1000) - (beam.bottomCover / 1000)
  const internalWidth = beam.width - 2 * (beam.sideCover / 1000)
  if (internalHeight <= 0) throw new Error(`Stirrup zone "${zone.label}": internal height (overallDepth − top − bottom cover) is ≤ 0.`)
  if (internalWidth <= 0) throw new Error(`Stirrup zone "${zone.label}": internal width (width − 2×side cover) is ≤ 0.`)

  const spacingM = spacing / 1000
  const numberOfSpaces = Math.ceil(zoneLength / spacingM)
  const numberOfStirrups = numberOfSpaces + (isFirstZone ? 1 : 0)
  const actualSpacing = zoneLength / numberOfSpaces

  const hookLengthM = hookLengthPerHook / 1000
  const outerStirrupLength = 2 * internalHeight + 2 * internalWidth + numberOfHooksPerStirrup * hookLengthM
  const numberOfCrossTies = (legs - 2) / 2
  const crossTieLength = internalHeight + 2 * hookLengthM
  const cuttingLength = outerStirrupLength + numberOfCrossTies * crossTieLength

  const totalLength = numberOfStirrups * cuttingLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight

  return {
    internalHeight, internalWidth, numberOfSpaces, numberOfStirrups, actualSpacing,
    outerStirrupLength, numberOfCrossTies, crossTieLength, cuttingLength, totalLength, unitWeight, grossWeight,
  }
}

/**
 * Full Rebar Beam calculation across multiple independently-dimensioned
 * beams, each with its own Longitudinal Groups and Stirrup Zones. Every
 * beam calculated fully independently — beams are NEVER combined before
 * calculation (same principle as Slab).
 *
 * Stirrup zones MUST cover the full beam length with zero gaps and zero
 * overlaps — this is a BLOCKING validation error, not a warning, because
 * any gap represents a real under-count of required stirrups.
 */
export function calculateRebarBeam(beams, longitudinalGroups, stirrupZones, allowances) {
  if (!Array.isArray(beams) || beams.length === 0) {
    throw new Error('At least one beam geometry must be selected.')
  }
  if ((!Array.isArray(longitudinalGroups) || longitudinalGroups.length === 0) && (!Array.isArray(stirrupZones) || stirrupZones.length === 0)) {
    throw new Error('At least one Longitudinal Group or Stirrup Zone must be defined.')
  }
  if (typeof allowances?.lapPct !== 'number' || Number.isNaN(allowances.lapPct)) {
    throw new Error('Lap/Splice Allowance % is required.')
  }
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) {
    throw new Error('Waste Allowance % is required.')
  }

  const beamBreakdown = []
  let theoretical = 0

  for (const beam of beams) {
    if (typeof beam.length !== 'number' || beam.length <= 0) {
      throw new Error(`Beam "${beam.label}": could not recover a valid Length from its geometry.`)
    }
    if (typeof beam.width !== 'number' || beam.width <= 0 || typeof beam.overallDepth !== 'number' || beam.overallDepth <= 0) {
      throw new Error(`Beam "${beam.label}": Width and Overall Depth are required.`)
    }

    // --- Mandatory full-length, gap-free, overlap-free zone coverage ---
    const sortedZones = [...(stirrupZones || [])].sort((a, b) => a.startOffset - b.startOffset)
    if (sortedZones.length > 0) {
      if (Math.abs(sortedZones[0].startOffset - 0) > 1e-9) {
        throw new Error(`Beam "${beam.label}": the first stirrup zone must start at 0 (starts at ${sortedZones[0].startOffset}).`)
      }
      if (Math.abs(sortedZones[sortedZones.length - 1].endOffset - beam.length) > 1e-9) {
        throw new Error(`Beam "${beam.label}": the last stirrup zone must end exactly at the beam length (${beam.length}m), but ends at ${sortedZones[sortedZones.length - 1].endOffset}m.`)
      }
      for (let i = 0; i < sortedZones.length - 1; i++) {
        if (Math.abs(sortedZones[i].endOffset - sortedZones[i + 1].startOffset) > 1e-9) {
          throw new Error(`Beam "${beam.label}": stirrup zones must cover the full beam length with no gaps or overlaps. Zone "${sortedZones[i].label}" ends at ${sortedZones[i].endOffset}m but zone "${sortedZones[i + 1].label}" starts at ${sortedZones[i + 1].startOffset}m.`)
        }
      }
    }

    const longitudinalResults = (longitudinalGroups || []).map((g) => ({ group: g, ...calculateLongitudinalGroup(beam, g) }))
    const stirrupResults = sortedZones.map((z, i) => ({ zone: z, ...calculateStirrupZone(beam, z, i === 0) }))

    const beamGross = longitudinalResults.reduce((s, r) => s + r.grossWeight, 0) + stirrupResults.reduce((s, r) => s + r.grossWeight, 0)
    beamBreakdown.push({ beam, longitudinalResults, stirrupResults, beamGross })
    theoretical += beamGross
  }

  const lapAllowance = theoretical * (allowances.lapPct / 100)
  const theoreticalPlusLap = theoretical + lapAllowance
  const wasteAllowance = theoreticalPlusLap * (allowances.wastePct / 100)
  const procurement = theoreticalPlusLap + wasteAllowance

  const steps = [
    { label: 'Theoretical Steel Weight', value: theoretical, unit: 'kg' },
    { label: `Lap/Splice Allowance (${allowances.lapPct}%, simplified estimating allowance)`, value: lapAllowance, unit: 'kg' },
    { label: 'Theoretical + Lap', value: theoreticalPlusLap, unit: 'kg' },
    { label: `Waste Allowance (${allowances.wastePct}%, manufacturing/cutting waste only)`, value: wasteAllowance, unit: 'kg' },
    { label: 'Procurement Weight', value: procurement, unit: 'kg' },
  ]

  return {
    beamBreakdown,
    steps,
    quantityBases: {
      theoretical: { label: 'Theoretical Steel Weight', valueKg: theoretical, valueTon: theoretical / 1000 },
      theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLap, valueTon: theoreticalPlusLap / 1000 },
      procurement: { label: 'Procurement Weight', valueKg: procurement, valueTon: procurement / 1000 },
    },
    finalUnit: 'ton',
  }
}

// =======================================================================
// Rebar Column — Pure Calculation Functions (FINAL LOCKED Contract)
//
// Architecturally distinct from both Slab (Layer/extentType model) and
// Beam (position-based Longitudinal Groups): Columns are vertical, and
// their transverse reinforcement is either discrete Ties (reused
// verbatim from Beam's Stirrup Zone formula) or a continuous Spiral
// (true helical-length formula) — the estimator explicitly chooses
// which, shape never implies it automatically.
// =======================================================================

/**
 * ONE longitudinal group for a column — bar count is always an explicit
 * estimator input (never derived from cover/perimeter), matching the
 * Beam principle: the system quantifies the estimator's design, it
 * never designs bar distribution around the perimeter itself.
 */
export function calculateColumnLongitudinalGroup(column, group) {
  const { diameter, numberOfBars } = group
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) {
    throw new Error(`Longitudinal group "${group.label}": bar diameter is required.`)
  }
  if (typeof numberOfBars !== 'number' || Number.isNaN(numberOfBars) || numberOfBars <= 0) {
    throw new Error(`Longitudinal group "${group.label}": number of bars must be a positive number.`)
  }
  const lapAllowanceM = (group.lapAllowanceLength || 0) / 1000
  const developmentLengthOverrideM = (group.developmentLengthOverride || 0) / 1000

  let barLength
  if (typeof group.explicitBarLength === 'number' && group.explicitBarLength > 0) {
    barLength = group.explicitBarLength
  } else {
    barLength = column.height + lapAllowanceM + developmentLengthOverrideM
    if (barLength <= 0) throw new Error(`Longitudinal group "${group.label}": derived bar length is ≤ 0.`)
  }

  const totalLength = numberOfBars * barLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight
  return { barLength, totalLength, unitWeight, grossWeight, usesLapAllowance: (group.lapAllowanceLength || 0) > 0 }
}

/**
 * ONE tie zone — reuses the Beam Stirrup Zone formula verbatim
 * (internal rectangle perimeter + cross-ties for legs beyond 2), with
 * the column's own width/depth (or diameter for a circular column
 * reinforced with rectangular-style ties) taking the place of the
 * beam's internal height/width.
 */
export function calculateColumnTieZone(column, zone, isFirstZone) {
  const { diameter, spacing, legs, numberOfHooksPerTie, hookLengthPerHook, startOffset, endOffset } = zone
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) throw new Error(`Tie zone "${zone.label}": diameter is required.`)
  if (typeof spacing !== 'number' || Number.isNaN(spacing) || spacing <= 0) throw new Error(`Tie zone "${zone.label}": spacing must be positive.`)
  if (legs < 2 || legs % 2 !== 0) throw new Error(`Tie zone "${zone.label}": legs must be an even number ≥ 2.`)
  if (typeof numberOfHooksPerTie !== 'number' || numberOfHooksPerTie <= 0) throw new Error(`Tie zone "${zone.label}": number of hooks per tie is required.`)
  if (typeof hookLengthPerHook !== 'number' || Number.isNaN(hookLengthPerHook) || hookLengthPerHook <= 0) throw new Error(`Tie zone "${zone.label}": hook length is required.`)
  const zoneLength = endOffset - startOffset
  if (zoneLength <= 0) throw new Error(`Tie zone "${zone.label}": end offset must be greater than start offset.`)

  const coverM = column.cover / 1000
  const internalWidth = (column.shape === 'circular' ? column.diameter : column.width) - 2 * coverM
  const internalHeight = (column.shape === 'circular' ? column.diameter : column.depth) - 2 * coverM
  if (internalWidth <= 0 || internalHeight <= 0) throw new Error(`Tie zone "${zone.label}": internal section dimension is ≤ 0 — cover too large.`)

  const spacingM = spacing / 1000
  const numberOfSpaces = Math.ceil(zoneLength / spacingM)
  const numberOfTies = numberOfSpaces + (isFirstZone ? 1 : 0)
  const actualSpacing = zoneLength / numberOfSpaces

  const hookLengthM = hookLengthPerHook / 1000
  const outerTieLength = 2 * internalHeight + 2 * internalWidth + numberOfHooksPerTie * hookLengthM
  const numberOfCrossTies = (legs - 2) / 2
  const crossTieLength = internalHeight + 2 * hookLengthM
  const cuttingLength = outerTieLength + numberOfCrossTies * crossTieLength

  const totalLength = numberOfTies * cuttingLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight

  return { internalWidth, internalHeight, numberOfSpaces, numberOfTies, actualSpacing, cuttingLength, totalLength, unitWeight, grossWeight }
}

/**
 * ONE spiral zone — TRUE helical length (Pythagorean helix), not the
 * simplified circumference × turns approximation. Continuous single
 * wire: adjacent zones never risk double-counting at a shared boundary
 * the way discrete tie counts can, because the length itself is a
 * simple sum of continuous segments — no "+1 for the first zone only"
 * adjustment is needed or applicable here.
 */
export function calculateColumnSpiralZone(column, zone) {
  const { diameter, pitch, startOffset, endOffset } = zone
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) throw new Error(`Spiral zone "${zone.label}": diameter is required.`)
  if (typeof pitch !== 'number' || Number.isNaN(pitch) || pitch <= 0) throw new Error(`Spiral zone "${zone.label}": pitch must be positive.`)
  const zoneLength = endOffset - startOffset
  if (zoneLength <= 0) throw new Error(`Spiral zone "${zone.label}": end offset must be greater than start offset.`)
  const pitchM = pitch / 1000
  if (pitchM >= zoneLength) throw new Error(`Spiral zone "${zone.label}": pitch must be smaller than the zone height.`)

  const coverM = column.cover / 1000
  const spiralDiameterM = diameter / 1000
  const centerlineDiameter = column.diameter - 2 * coverM - spiralDiameterM
  if (centerlineDiameter <= 0) throw new Error(`Spiral zone "${zone.label}": centreline diameter is ≤ 0 — cover/spiral diameter too large.`)

  const numberOfTurns = zoneLength / pitchM
  // True helical length per turn: sqrt((circumference)^2 + pitch^2) —
  // NOT a documented simplification, this is the exact geometric length.
  const helicalLength = numberOfTurns * Math.sqrt(Math.pow(Math.PI * centerlineDiameter, 2) + Math.pow(pitchM, 2))
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = helicalLength * unitWeight

  return { centerlineDiameter, numberOfTurns, helicalLength, unitWeight, grossWeight }
}

/**
 * Full Rebar Column calculation across multiple independently-configured
 * columns. Each column calculated fully independently — never merged.
 * transverseType is an explicit estimator choice, never implied by shape.
 */
export function calculateRebarColumn(columns, longitudinalGroups, tieZones, spiralZones, allowances) {
  if (!Array.isArray(columns) || columns.length === 0) throw new Error('At least one column must be selected.')
  if (!Array.isArray(longitudinalGroups) || longitudinalGroups.length === 0) throw new Error('At least one longitudinal reinforcement group must be defined.')
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) throw new Error('Waste Allowance % is required.')

  // LOCKED double-counting guard: if any longitudinal group uses an
  // explicit lapAllowanceLength, the general lapPct MUST be treated as
  // zero for this Output — the two mechanisms must never stack.
  const anyExplicitLap = longitudinalGroups.some((g) => (g.lapAllowanceLength || 0) > 0)
  const effectiveLapPct = anyExplicitLap ? 0 : (allowances?.lapPct ?? 0)
  if (!anyExplicitLap && (typeof allowances?.lapPct !== 'number' || Number.isNaN(allowances.lapPct))) {
    throw new Error('Lap/Splice Allowance % is required (or use an explicit Lap Allowance Length per group instead).')
  }

  const columnBreakdown = []
  let theoretical = 0

  for (const column of columns) {
    if (typeof column.height !== 'number' || column.height <= 0) throw new Error(`Column "${column.label}": height is required.`)
    if (column.shape === 'circular') {
      if (typeof column.diameter !== 'number' || column.diameter <= 0) throw new Error(`Column "${column.label}": diameter is required for a circular column.`)
    } else {
      if (typeof column.width !== 'number' || column.width <= 0 || typeof column.depth !== 'number' || column.depth <= 0) {
        throw new Error(`Column "${column.label}": Width and Depth are required for a rectangular/square column.`)
      }
    }

    const longitudinalResults = longitudinalGroups.map((g) => ({ group: g, ...calculateColumnLongitudinalGroup(column, g) }))
    let transverseResults = []
    let transverseGross = 0

    if (column.transverseType === 'ties') {
      const sortedZones = [...(tieZones || [])].sort((a, b) => a.startOffset - b.startOffset)
      if (sortedZones.length === 0) throw new Error(`Column "${column.label}": at least one Tie Zone is required.`)
      validateZoneCoverage(column, sortedZones, 'Tie')
      transverseResults = sortedZones.map((z, i) => ({ zone: z, kind: 'tie', ...calculateColumnTieZone(column, z, i === 0) }))
      transverseGross = transverseResults.reduce((s, r) => s + r.grossWeight, 0)
    } else if (column.transverseType === 'spiral') {
      const sortedZones = [...(spiralZones || [])].sort((a, b) => a.startOffset - b.startOffset)
      if (sortedZones.length === 0) throw new Error(`Column "${column.label}": at least one Spiral Zone is required.`)
      validateZoneCoverage(column, sortedZones, 'Spiral')
      transverseResults = sortedZones.map((z) => ({ zone: z, kind: 'spiral', ...calculateColumnSpiralZone(column, z) }))
      transverseGross = transverseResults.reduce((s, r) => s + r.grossWeight, 0)
    } else {
      throw new Error(`Column "${column.label}": transverse reinforcement type (Ties or Spiral) must be selected.`)
    }

    const longitudinalGross = longitudinalResults.reduce((s, r) => s + r.grossWeight, 0)
    const columnGross = longitudinalGross + transverseGross
    columnBreakdown.push({ column, longitudinalResults, transverseResults, longitudinalGross, transverseGross, columnGross })
    theoretical += columnGross
  }

  const lapAllowance = theoretical * (effectiveLapPct / 100)
  const theoreticalPlusLap = theoretical + lapAllowance
  const wasteAllowance = theoreticalPlusLap * (allowances.wastePct / 100)
  const procurement = theoreticalPlusLap + wasteAllowance

  const steps = [
    { label: 'Theoretical Steel Weight', value: theoretical, unit: 'kg' },
    { label: anyExplicitLap ? 'Lap/Splice Allowance (0% — explicit Lap Allowance Length used per group instead)' : `Lap/Splice Allowance (${effectiveLapPct}%, simplified estimating allowance)`, value: lapAllowance, unit: 'kg' },
    { label: 'Theoretical + Lap', value: theoreticalPlusLap, unit: 'kg' },
    { label: `Waste Allowance (${allowances.wastePct}%, manufacturing/cutting waste only)`, value: wasteAllowance, unit: 'kg' },
    { label: 'Procurement Weight', value: procurement, unit: 'kg' },
  ]

  return {
    columnBreakdown,
    steps,
    anyExplicitLap,
    quantityBases: {
      theoretical: { label: 'Theoretical Steel Weight', valueKg: theoretical, valueTon: theoretical / 1000 },
      theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLap, valueTon: theoreticalPlusLap / 1000 },
      procurement: { label: 'Procurement Weight', valueKg: procurement, valueTon: procurement / 1000 },
    },
    finalUnit: 'ton',
  }
}

function validateZoneCoverage(column, sortedZones, kindLabel) {
  if (Math.abs(sortedZones[0].startOffset - 0) > 1e-9) {
    throw new Error(`Column "${column.label}": the first ${kindLabel} zone must start at 0 (starts at ${sortedZones[0].startOffset}).`)
  }
  if (Math.abs(sortedZones[sortedZones.length - 1].endOffset - column.height) > 1e-9) {
    throw new Error(`Column "${column.label}": the last ${kindLabel} zone must end exactly at the column height (${column.height}m), but ends at ${sortedZones[sortedZones.length - 1].endOffset}m.`)
  }
  for (let i = 0; i < sortedZones.length - 1; i++) {
    if (Math.abs(sortedZones[i].endOffset - sortedZones[i + 1].startOffset) > 1e-9) {
      throw new Error(`Column "${column.label}": ${kindLabel} zones must cover the full column height with no gaps or overlaps. Zone "${sortedZones[i].label}" ends at ${sortedZones[i].endOffset}m but zone "${sortedZones[i + 1].label}" starts at ${sortedZones[i + 1].startOffset}m.`)
    }
  }
}

MODULE_REGISTRY.rebar_column = {
  moduleKey: 'rebar_column',
  family: 'rebar',
  label: 'Rebar — Column',
  discipline: 'structural',
  geometrySource: 'count_multi',
  outputUnit: 'ton',
  isDedicatedPanel: true,
  explanation: {
    method: 'Longitudinal bars: an explicit bar count you determine around the section perimeter, at Height + optional Lap Allowance Length + optional Development Length Override (or an explicit length you enter directly). '
      + 'Ties: discrete stirrups counted per Zone (same formula as Rebar Beam stirrups — outer perimeter + internal cross-ties for legs beyond 2), with the shared boundary between adjacent zones never double-counted. '
      + 'Spiral: a continuous helical wire using the TRUE helical length formula — turns × √((π×centreline diameter)² + pitch²) — not the simplified circumference-only approximation. '
      + 'You explicitly choose Ties or Spiral; it is never assumed from the column shape.',
    assumptions: [
      'Number of Bars is the count YOU determine around the perimeter — this module quantifies your design, it never designs bar distribution itself.',
      'A Lap Allowance Length on any longitudinal group disables the general Lap/Splice % for the whole Output, to prevent double-counting the same lap.',
      'Development Length is a manual override only — never automatically designed.',
      'Tie/Spiral zones must cover the full column height with zero gaps and zero overlaps — strictly enforced.',
      'Square columns are simply rectangular columns where width equals depth.',
      'Openings/embedments are not automatically compensated — represent any required reinforcement explicitly.',
    ],
    inputSources: [
      { label: 'Column location/count', source: 'Takeoff Geometry (Count/point measurement)' },
      { label: 'Height, section dimensions, cover, shape, transverse type', source: 'Estimator input (not measurable from a plan-view drawing)' },
      { label: 'Bar counts, diameters, zone offsets, spacing, pitch, hooks', source: 'Estimator input' },
      { label: 'Steel Unit Weight', source: 'Physical constant: π × (diameter/1000)² ÷ 4 × 7850 kg/m³ steel density' },
    ],
    glossary: [
      { term: 'Shape (Rectangular/Circular)', definition: 'Determines which geometry formulas apply — a square column is simply a rectangular one with equal width and depth.' },
      { term: 'Transverse Type (Ties/Spiral)', definition: 'Ties are discrete closed stirrups; Spiral is one continuous helical wire — you choose which, it is never assumed from the column shape.' },
      { term: 'Lap Allowance Length', definition: 'An explicit extra length added to a bar to represent overlap with the next storey\'s reinforcement — using this disables the general Lap % to avoid counting the same overlap twice.' },
      { term: 'Pitch', definition: 'The vertical distance the spiral advances per full turn — smaller pitch means more turns and more steel.' },
      { term: 'Centreline Diameter', definition: 'The diameter measured through the centre of the spiral wire itself, after deducting cover and half the spiral bar thickness from the column diameter.' },
      { term: 'Legs', definition: 'How many vertical tie legs cross the section — a tie with more than 2 legs is fabricated as an outer tie plus separate internal cross-ties.' },
      { term: 'Theoretical / Procurement', definition: 'Theoretical = steel physically required with no allowances. Procurement = Theoretical + Lap + Waste, i.e. what you would actually purchase.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const geometryCount = breakdownSnapshot?.geometryCount ?? 1
    const perColumnGross = breakdownSnapshot?.columnBreakdown?.[0]?.columnGross ?? 0
    const anyExplicitLap = (engineeringInputs?.longitudinalGroups || []).some((g) => Number(g.lapAllowanceLength || 0) > 0)

    const geometrySection = {
      title: `Geometry Quantity: ${geometryCount} No.`,
      rows: [row('Identical Columns Represented', `${geometryCount} No.`, { emphasis: true })],
    }

    const sections = (breakdownSnapshot?.columnBreakdown || []).map((cb) => ({
      title: `Per-Column Engineering Calculation — ${cb.column.label} (${cb.column.shape}, Height ${cb.column.height?.toFixed?.(2)} m)`,
      rows: [
        row('Section', cb.column.shape === 'circular' ? `Diameter ${cb.column.diameter?.toFixed?.(3)} m` : `Width × Depth: ${cb.column.width?.toFixed?.(3)} × ${cb.column.depth?.toFixed?.(3)} m`),
        row('Cover', `${cb.column.cover} mm`),
        row('Transverse Type', cb.column.transverseType === 'spiral' ? 'Spiral' : 'Ties'),
      ],
      subsections: [
        ...cb.longitudinalResults.map((lr) => ({
          title: `${lr.group.label || 'Longitudinal Group'} (Ø${lr.group.diameter}mm × ${lr.group.numberOfBars})`,
          rows: [
            ...(lr.group.explicitBarLength > 0 ? [row('Explicit Bar Length (overrides derived length)', `${lr.group.explicitBarLength} m`)] : []),
            ...(lr.group.lapAllowanceLength > 0 ? [row('Lap Allowance Length (disables global Lap %)', `${lr.group.lapAllowanceLength} mm`)] : []),
            ...(lr.group.developmentLengthOverride > 0 ? [row('Development Length Override', `${lr.group.developmentLengthOverride} mm`)] : []),
            row('Bar Length', `${lr.barLength?.toFixed?.(3)} m`),
            row('Group Weight', `${lr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
          ],
        })),
        ...cb.transverseResults.map((tr) => ({
          title: `${tr.zone.label || 'Transverse Zone'} (${tr.kind === 'tie' ? 'Tie' : 'Spiral'}, Ø${tr.zone.diameter}mm)`,
          rows: tr.kind === 'tie'
            ? [
                row('Zone Extent (Start → End)', `${tr.zone.startOffset}m → ${tr.zone.endOffset}m`),
                row('Legs', tr.zone.legs),
                row('Hooks per Tie / Hook Length', `${tr.zone.numberOfHooksPerTie} × ${tr.zone.hookLengthPerHook}mm`),
                row('Number of Ties', tr.numberOfTies),
                row('Actual Spacing', `${(tr.actualSpacing * 1000)?.toFixed?.(2)} mm`),
                row('Zone Weight', `${tr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
              ]
            : [
                row('Zone Extent (Start → End)', `${tr.zone.startOffset}m → ${tr.zone.endOffset}m`),
                row('Pitch', `${tr.zone.pitch} mm`),
                row('Centreline Diameter', `${tr.centerlineDiameter?.toFixed?.(3)} m`),
                row('Number of Turns', tr.numberOfTurns?.toFixed?.(2)),
                row('Helical Length', `${tr.helicalLength?.toFixed?.(3)} m`),
                row('Zone Weight', `${tr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
              ],
        })),
      ],
      footer: { label: 'Weight / Column', value: `${perColumnGross?.toFixed?.(2)} kg` },
    }))

    const totalSection = {
      title: 'Total Engineering Quantity',
      rows: [
        { label: `Total Columns`, value: `${geometryCount} No.` },
        { label: `Total Reinforcement Weight = ${perColumnGross?.toFixed?.(2)} kg × ${geometryCount}`, value: `${(perColumnGross * geometryCount)?.toFixed?.(2)} kg`, emphasis: true },
      ],
    }

    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    const basisLabels = { theoretical: 'Theoretical Steel Weight', theoreticalPlusLap: 'Theoretical + Lap', procurement: 'Procurement Weight' }
    const chosenBasis = engineeringInputs?.quantityBasisChosen
    const chosen = breakdownSnapshot?.quantityBases?.[chosenBasis]

    return {
      sections: [{ title: geometrySection.title, rows: geometrySection.rows }, ...sections, { title: totalSection.title, rows: totalSection.rows }],
      summary: { title: 'Calculation Summary', rows: summaryRows },
      selectedBasis: chosen ? { label: `Selected Quantity Basis: ${basisLabels[chosenBasis] || chosenBasis}`, value: `${chosen.valueTon?.toFixed?.(3)} ton` } : null,
    }
  },
}

// =======================================================================
// Rebar Foundation — Pure Calculation Functions (LOCKED Contract)
//
// Reuses calculateSlabLayerForSlab() and calculateUnitWeight() VERBATIM
// as the single source of truth for area-distributed reinforcement —
// no forked/duplicated copy of that logic exists here. Foundation adds
// only: an explicit foundationType (determining geometry source and
// input fields), and optional Dowel/Starter bar groups (a genuinely new
// concept Slab never needed).
// =======================================================================

/**
 * ONE dowel/starter bar group — an explicit estimator-specified
 * reinforcement group connecting a footing to the column above it.
 * This is never automatically designed; the estimator enters exactly
 * what they intend to embed and project.
 */
export function calculateDowelGroup(group) {
  const { diameter, numberOfBars, embedmentDepthInFooting, projectionAboveFooting } = group
  if (typeof diameter !== 'number' || Number.isNaN(diameter) || diameter <= 0) throw new Error(`Dowel group "${group.label}": diameter is required.`)
  if (typeof numberOfBars !== 'number' || Number.isNaN(numberOfBars) || numberOfBars <= 0) throw new Error(`Dowel group "${group.label}": number of bars must be positive.`)
  if (typeof embedmentDepthInFooting !== 'number' || embedmentDepthInFooting <= 0) throw new Error(`Dowel group "${group.label}": embedment depth is required.`)
  if (typeof projectionAboveFooting !== 'number' || projectionAboveFooting <= 0) throw new Error(`Dowel group "${group.label}": projection above footing is required.`)

  const totalBarLength = (embedmentDepthInFooting + projectionAboveFooting) / 1000
  const totalLength = numberOfBars * totalBarLength
  const unitWeight = calculateUnitWeight(diameter)
  const grossWeight = totalLength * unitWeight
  return { totalBarLength, totalLength, unitWeight, grossWeight }
}

/**
 * Full Rebar Foundation calculation. `foundationType` only affects which
 * geometry fields are meaningful (validated below) — the reinforcement
 * calculation itself is IDENTICAL to Slab's area-distributed layer
 * logic for every type, called through the exact same shared function.
 */
export function calculateRebarFoundation(foundations, layers, dowelGroups, allowances) {
  if (!Array.isArray(foundations) || foundations.length === 0) throw new Error('At least one foundation geometry must be selected.')
  if ((!Array.isArray(layers) || layers.length === 0) && (!Array.isArray(dowelGroups) || dowelGroups.length === 0)) {
    throw new Error('At least one reinforcement Layer or Dowel Group must be defined.')
  }
  if (typeof allowances?.lapPct !== 'number' || Number.isNaN(allowances.lapPct)) throw new Error('Lap/Splice Allowance % is required.')
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) throw new Error('Waste Allowance % is required.')

  const foundationBreakdown = []
  let theoretical = 0

  for (const foundation of foundations) {
    if (typeof foundation.length !== 'number' || foundation.length <= 0 || typeof foundation.width !== 'number' || foundation.width <= 0) {
      throw new Error(`Foundation "${foundation.label}": Length and Width are required.`)
    }

    const layerResults = []
    let layersGross = 0
    for (const layer of layers || []) {
      const cover = layer.coverPosition === 'top' ? foundation.topCover : foundation.bottomCover
      let parallelDim, perpendicularDim
      if (layer.extentType === 'local') {
        if (!layer.localExtent || typeof layer.localExtent.barLengthDim !== 'number' || layer.localExtent.barLengthDim <= 0
          || typeof layer.localExtent.distributionWidthDim !== 'number' || layer.localExtent.distributionWidthDim <= 0) {
          throw new Error(`Layer "${layer.label}": local extent requires a positive Bar Length and Distribution Width.`)
        }
        parallelDim = layer.localExtent.barLengthDim
        perpendicularDim = layer.localExtent.distributionWidthDim
      } else {
        parallelDim = layer.direction === 'X' ? foundation.length : foundation.width
        perpendicularDim = layer.direction === 'X' ? foundation.width : foundation.length
      }
      const openings = layer.extentType === 'full_slab' ? (foundation.openings || []) : []
      // SAME shared primitive used by Slab — not forked or duplicated.
      const calc = calculateSlabLayerForSlab(parallelDim, perpendicularDim, { ...layer, cover }, openings)
      layerResults.push({ layer, cover, ...calc })
      layersGross += calc.grossWeight
    }

    const dowelResults = (dowelGroups || []).map((g) => ({ group: g, ...calculateDowelGroup(g) }))
    const dowelsGross = dowelResults.reduce((s, r) => s + r.grossWeight, 0)

    const foundationGross = layersGross + dowelsGross
    foundationBreakdown.push({ foundation, layerResults, dowelResults, layersGross, dowelsGross, foundationGross })
    theoretical += foundationGross
  }

  const lapAllowance = theoretical * (allowances.lapPct / 100)
  const theoreticalPlusLap = theoretical + lapAllowance
  const wasteAllowance = theoreticalPlusLap * (allowances.wastePct / 100)
  const procurement = theoreticalPlusLap + wasteAllowance

  const steps = [
    { label: 'Theoretical Steel Weight', value: theoretical, unit: 'kg' },
    { label: `Lap/Splice Allowance (${allowances.lapPct}%, simplified estimating allowance)`, value: lapAllowance, unit: 'kg' },
    { label: 'Theoretical + Lap', value: theoreticalPlusLap, unit: 'kg' },
    { label: `Waste Allowance (${allowances.wastePct}%, manufacturing/cutting waste only)`, value: wasteAllowance, unit: 'kg' },
    { label: 'Procurement Weight', value: procurement, unit: 'kg' },
  ]

  return {
    foundationBreakdown, steps,
    quantityBases: {
      theoretical: { label: 'Theoretical Steel Weight', valueKg: theoretical, valueTon: theoretical / 1000 },
      theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLap, valueTon: theoreticalPlusLap / 1000 },
      procurement: { label: 'Procurement Weight', valueKg: procurement, valueTon: procurement / 1000 },
    },
    finalUnit: 'ton',
  }
}

// =======================================================================
// Rebar Wall — Pure Calculation Functions (LOCKED Contract)
//
// Also reuses calculateSlabLayerForSlab() VERBATIM. A wall is treated as
// a vertical "slab": Length x Height plane, with Face1/Face2 replacing
// Slab's Top/Bottom, and Horizontal/Vertical replacing X/Y. Same
// Openings Model A, same extentType/localExtent — nothing new invented,
// only re-oriented.
// =======================================================================

export function calculateRebarWall(walls, layers, allowances) {
  if (!Array.isArray(walls) || walls.length === 0) throw new Error('At least one wall geometry must be selected.')
  if (!Array.isArray(layers) || layers.length === 0) throw new Error('At least one reinforcement layer must be defined.')
  if (typeof allowances?.lapPct !== 'number' || Number.isNaN(allowances.lapPct)) throw new Error('Lap/Splice Allowance % is required.')
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) throw new Error('Waste Allowance % is required.')

  const wallBreakdown = []
  let theoretical = 0

  for (const wall of walls) {
    if (typeof wall.length !== 'number' || wall.length <= 0 || typeof wall.height !== 'number' || wall.height <= 0) {
      throw new Error(`Wall "${wall.label}": Length and Height are required.`)
    }
    const layerResults = []
    let wallGross = 0
    for (const layer of layers) {
      const cover = layer.face === 'face1' ? wall.face1Cover : wall.face2Cover
      let parallelDim, perpendicularDim
      if (layer.extentType === 'local') {
        if (!layer.localExtent || typeof layer.localExtent.barLengthDim !== 'number' || layer.localExtent.barLengthDim <= 0
          || typeof layer.localExtent.distributionWidthDim !== 'number' || layer.localExtent.distributionWidthDim <= 0) {
          throw new Error(`Layer "${layer.label}": local extent requires a positive Bar Length and Distribution Width.`)
        }
        parallelDim = layer.localExtent.barLengthDim
        perpendicularDim = layer.localExtent.distributionWidthDim
      } else {
        // Horizontal bars run ALONG the wall length, distributed UP the
        // height; Vertical bars run ALONG the height, distributed ACROSS
        // the length — the direct wall-oriented equivalent of Slab's X/Y.
        parallelDim = layer.direction === 'horizontal' ? wall.length : wall.height
        perpendicularDim = layer.direction === 'horizontal' ? wall.height : wall.length
      }
      const openings = layer.extentType === 'full_slab' ? (wall.openings || []) : []
      // SAME shared primitive used by Slab/Foundation — not duplicated.
      const calc = calculateSlabLayerForSlab(parallelDim, perpendicularDim, { ...layer, cover }, openings)
      layerResults.push({ layer, cover, ...calc })
      wallGross += calc.grossWeight
    }
    wallBreakdown.push({ wall, layerResults, wallGross })
    theoretical += wallGross
  }

  const lapAllowance = theoretical * (allowances.lapPct / 100)
  const theoreticalPlusLap = theoretical + lapAllowance
  const wasteAllowance = theoreticalPlusLap * (allowances.wastePct / 100)
  const procurement = theoreticalPlusLap + wasteAllowance

  const steps = [
    { label: 'Theoretical Steel Weight', value: theoretical, unit: 'kg' },
    { label: `Lap/Splice Allowance (${allowances.lapPct}%, simplified estimating allowance)`, value: lapAllowance, unit: 'kg' },
    { label: 'Theoretical + Lap', value: theoreticalPlusLap, unit: 'kg' },
    { label: `Waste Allowance (${allowances.wastePct}%, manufacturing/cutting waste only)`, value: wasteAllowance, unit: 'kg' },
    { label: 'Procurement Weight', value: procurement, unit: 'kg' },
  ]

  return {
    wallBreakdown, steps,
    quantityBases: {
      theoretical: { label: 'Theoretical Steel Weight', valueKg: theoretical, valueTon: theoretical / 1000 },
      theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLap, valueTon: theoreticalPlusLap / 1000 },
      procurement: { label: 'Procurement Weight', valueKg: procurement, valueTon: procurement / 1000 },
    },
    finalUnit: 'ton',
  }
}

MODULE_REGISTRY.rebar_foundation = {
  moduleKey: 'rebar_foundation', label: 'Rebar — Foundation', discipline: 'structural', family: 'rebar',
  geometrySource: 'area_or_length_multi', outputUnit: 'ton', isDedicatedPanel: true,
  explanation: {
    method: 'Reinforcement uses the SAME area-distribution formula as Rebar Slab (bars = ceil(effective width ÷ spacing) + 1), reused verbatim — a foundation is treated as an area of reinforcement, exactly like a slab. '
      + 'Foundation Type (Isolated/Combined/Strip/Raft/Pile Cap) only changes which geometry fields apply — Strip footings use a manually-entered width; the others use the Length×Width recovered from the drawn rectangle. '
      + 'Optional Dowel/Starter groups add explicit vertical bars connecting the foundation to the column above.',
    assumptions: [
      'Pile reinforcement and Pedestals are not separate Foundation types — they are quantified using the existing Rebar Column module instead.',
      'Local Extent handles negative-moment top reinforcement (e.g. between columns in a Combined Footing) or column-line strips (e.g. in a Raft) — the same mechanism as Rebar Slab.',
      'Openings deduct intersecting bars only (Model A) — no automatic trimming reinforcement.',
      'Non-rectangular Pile Caps (e.g. triangular for 3-pile groups) are approximated by the estimator using a representative rectangle.',
      'Dowel/Starter bars are never automatically designed — the estimator enters exactly what they intend to embed and project.',
    ],
    inputSources: [
      { label: 'Length/Width (Isolated/Combined/Raft/Pile Cap)', source: 'Takeoff Geometry (Canvas Rectangle + calibration)' },
      { label: 'Width (Strip only)', source: 'Estimator input (manual, alongside a Length takeoff measurement)' },
      { label: 'Diameter, spacing, covers, dowel dimensions', source: 'Estimator input' },
      { label: 'Steel Unit Weight', source: 'Physical constant: π × (diameter/1000)² ÷ 4 × 7850 kg/m³ steel density' },
    ],
    glossary: [
      { term: 'Foundation Type', definition: 'Isolated (single column), Combined (shared by 2+ columns), Strip (continuous under a wall), Raft/Mat (large slab under the whole building), or Pile Cap (over a group of piles) — this only changes which geometry inputs apply.' },
      { term: 'Local Extent', definition: 'A reinforcement zone smaller than the full foundation (e.g. extra top bars between two columns in a Combined Footing) — defined by its own bar length and distribution width.' },
      { term: 'Dowel / Starter Bars', definition: 'Vertical bars embedded in the foundation and projecting above it to connect with the column reinforcement above — entered explicitly by the estimator, never designed automatically.' },
      { term: 'Theoretical / Procurement', definition: 'Theoretical = steel physically required with no allowances. Procurement = Theoretical + Lap + Waste, i.e. what you would actually purchase.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.foundationBreakdown || []).map((fb) => ({
      title: `${fb.foundation.label} — ${fb.foundation.length?.toFixed?.(2)} × ${fb.foundation.width?.toFixed?.(2)} m`,
      rows: [
        row('Foundation Type', ({ isolated: 'Isolated Footing', combined: 'Combined Footing', strip: 'Strip Footing', raft: 'Raft / Mat Foundation', pile_cap: 'Pile Cap' })[fb.foundation.foundationType] || fb.foundation.foundationType),
        row('Covers (Top / Bottom)', `${fb.foundation.topCover} / ${fb.foundation.bottomCover} mm`),
      ],
      subsections: [
        ...fb.layerResults.map((lr) => ({
          title: `${lr.layer.label} (${lr.layer.direction}, Ø${lr.layer.diameter}mm, ${lr.layer.extentType === 'local' ? 'Local Extent' : 'Full'})`,
          rows: [
            row('Cover Position', lr.layer.coverPosition),
            row('Input Spacing', `${lr.layer.spacing} mm`),
            ...(lr.layer.extentType === 'local' ? [row('Local Extent (Bar Length × Distribution Width)', `${lr.layer.localExtent?.barLengthDim} × ${lr.layer.localExtent?.distributionWidthDim} m`)] : []),
            row('Number of Bars', lr.finalNumberOfBars),
            row('Actual Spacing', `${(lr.actualSpacing * 1000)?.toFixed?.(2)} mm`),
            row('Weight', `${lr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
          ],
        })),
        ...fb.dowelResults.map((dr) => ({
          title: dr.group.label,
          rows: [
            row('Diameter × Count', `Ø${dr.group.diameter}mm × ${dr.group.numberOfBars}`),
            row('Embedment / Projection', `${dr.group.embedmentDepthInFooting} / ${dr.group.projectionAboveFooting} mm`),
            row('Bar Length', `${dr.totalBarLength?.toFixed?.(3)} m`),
            row('Weight', `${dr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
          ],
        })),
      ],
      footer: { label: 'Foundation Total', value: `${fb.foundationGross?.toFixed?.(2)} kg` },
    }))
    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    const basisLabels = { theoretical: 'Theoretical Steel Weight', theoreticalPlusLap: 'Theoretical + Lap', procurement: 'Procurement Weight' }
    const chosen = breakdownSnapshot?.quantityBases?.[engineeringInputs?.quantityBasisChosen]
    return { sections, summary: { title: 'Calculation Summary', rows: summaryRows }, selectedBasis: chosen ? { label: `Selected Quantity Basis: ${basisLabels[engineeringInputs?.quantityBasisChosen]}`, value: `${chosen.valueTon?.toFixed?.(3)} ton` } : null }
  },
}

MODULE_REGISTRY.rebar_wall = {
  moduleKey: 'rebar_wall', label: 'Rebar — Wall', discipline: 'structural', family: 'rebar',
  geometrySource: 'length_multi', outputUnit: 'ton', isDedicatedPanel: true,
  explanation: {
    method: 'A wall is treated as a vertical slab: reinforcement uses the SAME area-distribution formula as Rebar Slab, reused verbatim, with Face 1/Face 2 replacing Top/Bottom and Horizontal/Vertical replacing X/Y. '
      + 'Horizontal bars run along the wall length, distributed up the height; Vertical bars run up the height, distributed along the length.',
    assumptions: [
      'Openings (doors, windows) deduct intersecting bars only (Model A) — no automatic trimming or boundary reinforcement around openings.',
      'Boundary/edge reinforcement, if required, must be entered explicitly as a separate Layer with Local Extent — it is never designed automatically.',
      'Starter/dowel bars connecting to a foundation or slab below are entered as their own Longitudinal-style groups if needed, not automatically generated.',
    ],
    inputSources: [
      { label: 'Wall Length', source: 'Takeoff Geometry (Canvas Length measurement)' },
      { label: 'Height, thickness, face covers', source: 'Estimator input (not measurable from a plan-view drawing)' },
      { label: 'Diameter, spacing, openings', source: 'Estimator input' },
      { label: 'Steel Unit Weight', source: 'Physical constant: π × (diameter/1000)² ÷ 4 × 7850 kg/m³ steel density' },
    ],
    glossary: [
      { term: 'Face 1 / Face 2', definition: 'The two exposed sides of the wall — each can have its own cover and its own set of horizontal/vertical reinforcement layers.' },
      { term: 'Horizontal / Vertical Reinforcement', definition: 'Horizontal bars run along the wall length; Vertical bars run up the wall height — a wall is reinforced in both directions, like a slab standing on its edge.' },
      { term: 'Openings (Model A)', definition: 'Any bar crossing an opening (door/window) is removed entirely. No trimming/edge reinforcement is added automatically — add it as a separate Layer if required.' },
      { term: 'Local Extent', definition: 'A reinforcement zone smaller than the full wall (e.g. extra bars around an opening or at a boundary) — defined by its own bar length and distribution width.' },
      { term: 'Theoretical / Procurement', definition: 'Theoretical = steel physically required with no allowances. Procurement = Theoretical + Lap + Waste, i.e. what you would actually purchase.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.wallBreakdown || []).map((wb) => ({
      title: `${wb.wall.label} — Length ${wb.wall.length?.toFixed?.(2)} m × Height ${wb.wall.height?.toFixed?.(2)} m`,
      rows: [
        row('Thickness', `${wb.wall.thickness} m`),
        row('Covers (Face 1 / Face 2)', `${wb.wall.face1Cover} / ${wb.wall.face2Cover} mm`),
        ...(wb.wall.openings?.length > 0 ? wb.wall.openings.map((o, oi) => row(`Opening ${oi + 1} (Offset / Extent)`, `${o.offsetAlongPerpendicular}m / ${o.extentAlongPerpendicular}m`)) : []),
      ],
      subsections: wb.layerResults.map((lr) => ({
        title: `${lr.layer.label} (${lr.layer.face}, ${lr.layer.direction}, Ø${lr.layer.diameter}mm)`,
        rows: [
          row('Input Spacing', `${lr.layer.spacing} mm`),
          ...(lr.layer.extentType === 'local' ? [row('Local Extent (Bar Length × Distribution Width)', `${lr.layer.localExtent?.barLengthDim} × ${lr.layer.localExtent?.distributionWidthDim} m`)] : []),
          row('Number of Bars', lr.numberOfBars),
          ...(lr.deductedBars > 0 ? [row('Deducted (Openings)', `${lr.deductedBars} → Final Bars: ${lr.finalNumberOfBars}`)] : []),
          row('Actual Spacing', `${(lr.actualSpacing * 1000)?.toFixed?.(2)} mm`),
          row('Weight', `${lr.grossWeight?.toFixed?.(2)} kg`, { emphasis: true }),
        ],
      })),
      footer: { label: 'Wall Total', value: `${wb.wallGross?.toFixed?.(2)} kg` },
    }))
    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    const basisLabels = { theoretical: 'Theoretical Steel Weight', theoreticalPlusLap: 'Theoretical + Lap', procurement: 'Procurement Weight' }
    const chosen = breakdownSnapshot?.quantityBases?.[engineeringInputs?.quantityBasisChosen]
    return { sections, summary: { title: 'Calculation Summary', rows: summaryRows }, selectedBasis: chosen ? { label: `Selected Quantity Basis: ${basisLabels[engineeringInputs?.quantityBasisChosen]}`, value: `${chosen.valueTon?.toFixed?.(3)} ton` } : null }
  },
}

// =======================================================================
// Surface Area — Pure Calculation Function (LOCKED Phase 2 Contract)
// =======================================================================
export function calculateSurfaceArea(surfaces, allowances) {
  if (!Array.isArray(surfaces) || surfaces.length === 0) throw new Error('At least one surface must be defined.')
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) throw new Error('Waste % is required.')

  const surfaceBreakdown = []
  let finalTotal = 0

  for (const s of surfaces) {
    if (!['masonry', 'plaster', 'waterproofing', 'flooring', 'painting', 'ceiling'].includes(s.type)) {
      throw new Error(`Surface "${s.label}": a valid type must be selected.`)
    }
    let grossArea
    if (s.area != null) {
      if (typeof s.area !== 'number' || s.area <= 0) throw new Error(`Surface "${s.label}": area must be positive.`)
      grossArea = s.area
    } else {
      if (typeof s.length !== 'number' || s.length <= 0 || typeof s.height !== 'number' || s.height <= 0) {
        throw new Error(`Surface "${s.label}": Length and Height are required.`)
      }
      grossArea = s.length * s.height
    }

    const openings = s.openings || []
    for (const o of openings) {
      if (typeof o.length !== 'number' || o.length <= 0 || typeof o.height !== 'number' || o.height <= 0) {
        throw new Error(`Surface "${s.label}", opening "${o.label}": length and height must be positive.`)
      }
    }
    const openingsArea = openings.reduce((sum, o) => sum + o.length * o.height, 0)
    if (openingsArea >= grossArea) throw new Error(`Surface "${s.label}": openings (${openingsArea.toFixed(2)} m²) equal or exceed gross area (${grossArea.toFixed(2)} m²).`)
    const netArea = grossArea - openingsArea

    let upstandArea = 0
    if (s.type === 'waterproofing') {
      if (typeof s.upstandHeight !== 'number' || s.upstandHeight <= 0) throw new Error(`Surface "${s.label}": Upstand Height is required for Waterproofing.`)
      if (s.length == null || s.height == null) throw new Error(`Surface "${s.label}": Waterproofing requires Length × Height geometry (not Area-only) to derive the perimeter for the upstand.`)
      const perimeter = 2 * (s.length + s.height)
      upstandArea = perimeter * s.upstandHeight
    }

    let numberOfCoats = 1
    if (s.type === 'painting') {
      if (typeof s.numberOfCoats !== 'number' || s.numberOfCoats <= 0) throw new Error(`Surface "${s.label}": Number of Coats is required for Painting.`)
      numberOfCoats = s.numberOfCoats
    }

    const totalBeforeWaste = netArea + upstandArea
    // LOCKED Waste semantic: waste is applied BEFORE the coats multiplier
    // — waste happens per coat, not once for the whole multi-coat job.
    const finalQuantity = (totalBeforeWaste * (1 + allowances.wastePct / 100)) * numberOfCoats

    surfaceBreakdown.push({ surface: s, grossArea, openingsArea, netArea, upstandArea, numberOfCoats, totalBeforeWaste, finalQuantity })
    finalTotal += finalQuantity
  }

  return {
    surfaceBreakdown,
    steps: [
      { label: 'Gross Area (before deductions)', value: surfaceBreakdown.reduce((s, r) => s + r.grossArea, 0), unit: 'm²' },
      { label: 'Openings Deducted', value: surfaceBreakdown.reduce((s, r) => s + r.openingsArea, 0), unit: 'm²' },
      { label: 'Net Area', value: surfaceBreakdown.reduce((s, r) => s + r.netArea, 0), unit: 'm²' },
      { label: `Waste Allowance (${allowances.wastePct}%, applied before Coats multiplier)`, value: surfaceBreakdown.reduce((s, r) => s + (r.totalBeforeWaste * (allowances.wastePct / 100)) * r.numberOfCoats, 0), unit: 'm²' },
      { label: 'Final Quantity', value: finalTotal, unit: 'm²' },
    ],
    finalQuantity: finalTotal,
    finalUnit: 'm2',
  }
}

// =======================================================================
// Concrete Volume — Pure Calculation Function (LOCKED Phase 2 Contract)
// =======================================================================
export function calculateConcreteVolume(volumes, allowances) {
  if (!Array.isArray(volumes) || volumes.length === 0) throw new Error('At least one concrete geometry must be defined.')
  if (typeof allowances?.wastePct !== 'number' || Number.isNaN(allowances.wastePct)) throw new Error('Waste % is required.')

  const volumeBreakdown = []
  let finalTotal = 0

  for (const v of volumes) {
    if (v.geometrySource !== 'area' && v.geometrySource !== 'length_width') {
      throw new Error(`Volume "${v.label}": geometrySource must be 'area' or 'length_width'.`)
    }
    let grossArea
    if (v.geometrySource === 'area') {
      if (typeof v.area !== 'number' || v.area <= 0) throw new Error(`Volume "${v.label}": area must be positive.`)
      grossArea = v.area
    } else {
      if (typeof v.length !== 'number' || v.length <= 0 || typeof v.width !== 'number' || v.width <= 0) {
        throw new Error(`Volume "${v.label}": Length and Width are required.`)
      }
      grossArea = v.length * v.width
    }
    if (typeof v.thickness !== 'number' || v.thickness <= 0) throw new Error(`Volume "${v.label}": thickness must be positive.`)

    const openings = v.openings || []
    for (const o of openings) {
      if (typeof o.length !== 'number' || o.length <= 0 || typeof o.width !== 'number' || o.width <= 0) {
        throw new Error(`Volume "${v.label}", opening "${o.label}": length and width must be positive.`)
      }
    }
    const openingsArea = openings.reduce((sum, o) => sum + o.length * o.width, 0)
    if (openingsArea >= grossArea) throw new Error(`Volume "${v.label}": openings (${openingsArea.toFixed(2)} m²) equal or exceed gross area (${grossArea.toFixed(2)} m²).`)
    const netArea = grossArea - openingsArea
    const netVolume = netArea * v.thickness * (1 + allowances.wastePct / 100)

    volumeBreakdown.push({ volume: v, grossArea, openingsArea, netArea, netVolume })
    finalTotal += netVolume
  }

  return {
    volumeBreakdown,
    steps: [
      { label: 'Gross Area', value: volumeBreakdown.reduce((s, r) => s + r.grossArea, 0), unit: 'm²' },
      { label: 'Openings Deducted', value: volumeBreakdown.reduce((s, r) => s + r.openingsArea, 0), unit: 'm²' },
      { label: `Waste Allowance (${allowances.wastePct}%)`, value: finalTotal - volumeBreakdown.reduce((s, r) => s + r.netArea * r.volume.thickness, 0), unit: 'm³' },
      { label: 'Final Volume', value: finalTotal, unit: 'm³' },
    ],
    finalQuantity: finalTotal,
    finalUnit: 'm3',
  }
}

// =======================================================================
// Formwork — Pure Calculation Function (LOCKED Phase 2 Contract)
// Column elementType follows the GLOBAL Count Semantic (LOCKED):
//   Per-Instance Quantity x Frozen Geometry Count = Total Quantity
// =======================================================================
export function calculateFormwork(elements) {
  if (!Array.isArray(elements) || elements.length === 0) throw new Error('At least one formwork element must be defined.')

  const elementBreakdown = []
  let finalTotal = 0

  for (const el of elements) {
    if (el.elementType === 'slab') {
      if (typeof el.length !== 'number' || el.length <= 0 || typeof el.width !== 'number' || el.width <= 0 || typeof el.depth !== 'number' || el.depth <= 0) {
        throw new Error(`Slab formwork "${el.label}": Length, Width and Depth are required.`)
      }
      const formedEdges = (el.formedEdges || []).filter((e) => e.included)
      const soffitArea = el.length * el.width
      const edgeArea = formedEdges.reduce((sum, e) => sum + (e.edge.startsWith('length') ? el.length : el.width) * el.depth, 0)
      const total = soffitArea + edgeArea
      elementBreakdown.push({ element: el, soffitArea, formedEdges, edgeArea, perInstance: total, geometryCount: 1, total })
      finalTotal += total

    } else if (el.elementType === 'beam') {
      if (typeof el.width !== 'number' || el.width <= 0 || typeof el.depth !== 'number' || el.depth <= 0 || typeof el.length !== 'number' || el.length <= 0) {
        throw new Error(`Beam formwork "${el.label}": Width, Depth and Length are required.`)
      }
      const total = (el.width + 2 * el.depth) * el.length
      elementBreakdown.push({ element: el, perInstance: total, geometryCount: 1, total })
      finalTotal += total

    } else if (el.elementType === 'column') {
      if (typeof el.height !== 'number' || el.height <= 0) throw new Error(`Column formwork "${el.label}": height is required.`)
      let perimeter
      if (el.shape === 'circular') {
        if (typeof el.diameter !== 'number' || el.diameter <= 0) throw new Error(`Column formwork "${el.label}": diameter is required for a circular column.`)
        perimeter = Math.PI * el.diameter
      } else {
        if (typeof el.width !== 'number' || el.width <= 0 || typeof el.depth !== 'number' || el.depth <= 0) {
          throw new Error(`Column formwork "${el.label}": Width and Depth are required for a rectangular/square column.`)
        }
        perimeter = 2 * (el.width + el.depth)
      }
      const perInstance = perimeter * el.height
      // GLOBAL Count Semantic — Column Formwork gets NO exception.
      const geometryCount = el.geometryCount ?? 0
      if (!(geometryCount > 0)) throw new Error(`Column formwork "${el.label}": Geometry Count must be greater than 0.`)
      const total = perInstance * geometryCount
      elementBreakdown.push({ element: el, perimeter, perInstance, geometryCount, total })
      finalTotal += total

    } else {
      throw new Error(`Formwork "${el.label}": elementType must be 'slab', 'beam', or 'column'.`)
    }
  }

  return {
    elementBreakdown,
    steps: [{ label: 'Total Formwork Area', value: finalTotal, unit: 'm²' }],
    finalQuantity: finalTotal,
    finalUnit: 'm2',
  }
}

// =======================================================================
// Excavation / Backfilling — Pure Calculation Function (LOCKED Phase 2)
// Physical excavation volume NEVER includes Waste% (LOCKED correction).
// Backfill requires EXPLICIT selection of permanent-volume deductions —
// never an automatic subtraction of every concrete output in scope.
// =======================================================================
export function calculateExcavation(input) {
  if (input.mode === 'excavation') {
    if (typeof input.area !== 'number' || input.area <= 0) throw new Error('Excavation: area must be positive.')
    if (typeof input.depth !== 'number' || input.depth <= 0) throw new Error('Excavation: depth must be positive.')
    const physicalVolume = input.area * input.depth
    return {
      mode: 'excavation',
      area: input.area, depth: input.depth, physicalVolume,
      steps: [
        { label: 'Plan Area', value: input.area, unit: 'm²' },
        { label: 'Excavation Depth', value: input.depth, unit: 'm' },
        { label: 'Physical Excavation Volume (no Waste applied)', value: physicalVolume, unit: 'm³' },
      ],
      finalQuantity: physicalVolume,
      finalUnit: 'm3',
    }
  }

  if (input.mode === 'backfilling') {
    if (typeof input.excavationVolume !== 'number' || input.excavationVolume <= 0) throw new Error('Backfilling: a valid Excavation source volume is required.')
    const permanentVolumes = input.permanentVolumes || [] // [{label, value}]
    const totalPermanent = permanentVolumes.reduce((s, p) => s + p.value, 0)
    const backfillVolume = input.excavationVolume - totalPermanent
    if (backfillVolume < 0) throw new Error(`Backfilling: selected permanent volumes (${totalPermanent.toFixed(2)} m³) exceed the excavation volume (${input.excavationVolume.toFixed(2)} m³).`)
    return {
      mode: 'backfilling',
      excavationVolume: input.excavationVolume, permanentVolumes, totalPermanent, backfillVolume,
      steps: [
        { label: 'Excavation Volume (source)', value: input.excavationVolume, unit: 'm³' },
        { label: 'Selected Permanent Volumes (deducted)', value: totalPermanent, unit: 'm³' },
        { label: 'Final Backfill Volume', value: backfillVolume, unit: 'm³' },
      ],
      finalQuantity: backfillVolume,
      finalUnit: 'm3',
    }
  }

  throw new Error('Excavation: mode must be "excavation" or "backfilling".')
}

MODULE_REGISTRY.surface_area = {
  moduleKey: 'surface_area', label: 'Surface Area (Masonry/Plaster/Waterproofing/Flooring/Painting/Ceiling)',
  discipline: 'finishes', family: 'finishes', geometrySource: 'area_or_length_height', outputUnit: 'm2', isDedicatedPanel: true,
  explanation: {
    method: 'Gross Area = the entered Area, or Length × Height. Openings are deducted exactly (Opening Length × Opening Height) — a precise physical deduction, unlike Rebar\'s bar-count approximation. '
      + 'Waterproofing adds an Upstand Area = perimeter (2×(Length+Height), always derived from the rectangular geometry) × Upstand Height. '
      + 'Painting multiplies by Number of Coats — Waste is applied BEFORE the coats multiplier (waste happens per coat): Final = (Net Area × (1+Waste%)) × Coats.',
    assumptions: [
      'Openings are assumed non-overlapping — overlap detection is OUT OF SCOPE; the estimator is responsible for ensuring entered openings do not physically overlap.',
      'Waterproofing upstand always uses a derived rectangular perimeter — there is no separate manual perimeter input; non-rectangular perimeters are out of scope.',
      'Multiple selected surfaces are calculated independently and summed — this is NOT a Count-based module.',
      'BOQ Usage: For a BOQ quantity, use 1 coat. Create a separate Output with the actual number of coats when the quantity is required for procurement or internal costing.',
    ],
    inputSources: [
      { label: 'Area, or Length × Height', source: 'Estimator input (Ceiling/Flooring commonly use Area directly; others use Length×Height)' },
      { label: 'Openings, Upstand Height, Number of Coats', source: 'Estimator input' },
      { label: 'Waste %', source: 'Estimator input (simplified allowance)' },
    ],
    glossary: [
      { term: 'Gross Area', definition: 'The full surface area before any deduction for openings.' },
      { term: 'Net Area', definition: 'Gross Area minus the area of all openings.' },
      { term: 'Upstand', definition: 'A vertical return of waterproofing at the edge of a surface (e.g. up a wall) — its area is perimeter × upstand height, added on top of the base area.' },
      { term: 'Number of Coats', definition: 'How many layers of paint are applied — the final quantity is the net (post-waste) area multiplied by this count.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.surfaceBreakdown || []).map((sb) => ({
      title: `${sb.surface.label} (${sb.surface.type})`,
      rows: [
        row('Geometry', sb.surface.area != null ? `Area: ${sb.surface.area} m²` : `Length × Height: ${sb.surface.length} × ${sb.surface.height} m`),
        row('Gross Area', `${sb.grossArea?.toFixed?.(2)} m²`),
        ...(sb.surface.openings?.length > 0 ? sb.surface.openings.map((o, oi) => row(`Opening ${oi + 1}`, `${o.length} × ${o.height} m = ${(o.length * o.height).toFixed(2)} m²`)) : []),
        row('Openings Deducted', `${sb.openingsArea?.toFixed?.(2)} m²`),
        row('Net Area', `${sb.netArea?.toFixed?.(2)} m²`),
        ...(sb.surface.type === 'waterproofing' ? [
          row('Upstand Height', `${sb.surface.upstandHeight} m`), row('Upstand Area', `${sb.upstandArea?.toFixed?.(2)} m²`),
          ...(sb.surface.systemSpecification ? [row('System Specification (informational only, does not affect quantity)', sb.surface.systemSpecification)] : []),
        ] : []),
        ...(sb.surface.type === 'painting' ? [row('Number of Coats', sb.numberOfCoats)] : []),
        row('Final Quantity', `${sb.finalQuantity?.toFixed?.(2)} m²`, { emphasis: true }),
      ],
    }))
    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    return { sections, summary: { title: 'Calculation Summary', rows: summaryRows }, selectedBasis: null }
  },
}

MODULE_REGISTRY.concrete_volume = {
  moduleKey: 'concrete_volume', label: 'Concrete Volume', discipline: 'concrete_earthwork', family: 'concrete_earthwork',
  geometrySource: 'area_or_length_width', outputUnit: 'm3', isDedicatedPanel: true,
  explanation: {
    method: 'Net Volume = (Gross Area − Opening Areas) × Thickness × (1+Waste%). Gross Area comes either directly from an Area geometry, or from Length × Width — this geometrySource is explicit per geometry item.',
    assumptions: [
      'No reinforcement logic is included — this module quantifies concrete volume only.',
      'Different thicknesses require separate, independently-traceable geometry items — they are never merged.',
      'Openings are assumed non-overlapping.',
      'BOQ Usage: For a BOQ quantity, use 0% waste. Create a separate Output with the applicable waste percentage when the quantity is required for procurement or internal costing.',
    ],
    inputSources: [
      { label: 'Area, or Length × Width', source: 'Estimator input / Takeoff Geometry' },
      { label: 'Thickness, Openings', source: 'Estimator input' },
      { label: 'Waste %', source: 'Estimator input' },
    ],
    glossary: [
      { term: 'geometrySource', definition: 'Whether this item\'s area comes directly from an entered Area value, or is derived from Length × Width.' },
      { term: 'Net Volume', definition: 'The final concrete volume after deducting openings and applying the thickness and waste allowance.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.volumeBreakdown || []).map((vb) => ({
      title: vb.volume.label,
      rows: [
        row('Geometry Source', vb.volume.geometrySource),
        row('Geometry', vb.volume.geometrySource === 'area' ? `Area: ${vb.volume.area} m²` : `Length × Width: ${vb.volume.length} × ${vb.volume.width} m`),
        row('Gross Area', `${vb.grossArea?.toFixed?.(2)} m²`),
        ...(vb.volume.openings?.length > 0 ? vb.volume.openings.map((o, oi) => row(`Opening ${oi + 1}`, `${o.length} × ${o.width} m = ${(o.length * o.width).toFixed(2)} m²`)) : []),
        row('Openings Deducted', `${vb.openingsArea?.toFixed?.(2)} m²`),
        row('Net Area', `${vb.netArea?.toFixed?.(2)} m²`),
        row('Thickness', `${vb.volume.thickness} m`),
        row('Net Volume', `${vb.netVolume?.toFixed?.(3)} m³`, { emphasis: true }),
      ],
    }))
    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    return { sections, summary: { title: 'Calculation Summary', rows: summaryRows }, selectedBasis: null }
  },
}

MODULE_REGISTRY.formwork = {
  moduleKey: 'formwork', label: 'Formwork (Slab/Beam/Column)', discipline: 'concrete_earthwork', family: 'concrete_earthwork',
  geometrySource: 'element_type_driven', outputUnit: 'm2', isDedicatedPanel: true,
  explanation: {
    method: 'Slab: Soffit Area (Length×Width) + Edge Formwork (only the explicitly-selected formed edges × Depth) — the estimator identifies which edges actually need formwork, never the full perimeter automatically. '
      + 'Beam: (Width + 2×Depth) × Length — the top face is always excluded. '
      + 'Column: Perimeter × Height — Rectangular uses 2×(Width+Depth); Circular uses π×Diameter. Column Formwork follows the Global Count Semantic: Per-Instance Formwork × Frozen Geometry Count = Total Formwork.',
    assumptions: [
      'Openings, interfaces with attached elements, and shared faces between adjacent elements are OUT OF SCOPE.',
      'Slab formed edges must be explicitly selected by the estimator — never assumed from the full perimeter.',
      'Different column configurations (e.g. C1 vs C2) must be separate takeoff items and separate Outputs — same rule as Rebar Column.',
    ],
    inputSources: [
      { label: 'Slab: Length, Width, Depth, Formed Edges', source: 'Estimator input' },
      { label: 'Beam: Width, Depth, Length', source: 'Estimator input' },
      { label: 'Column: Shape, Width/Depth or Diameter, Height, Geometry Count', source: 'Estimator input / Takeoff Geometry (count)' },
    ],
    glossary: [
      { term: 'Formed Edges', definition: 'The specific slab edges that actually require side formwork (some edges may adjoin another slab and need none) — selected explicitly, not assumed from the full perimeter.' },
      { term: 'Geometry Count (Column)', definition: 'The number of identical columns this configuration represents — the per-instance formwork area is multiplied by this frozen count, exactly as in Rebar Column.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.elementBreakdown || []).map((eb) => {
      const el = eb.element
      if (el.elementType === 'slab') {
        return { title: `${el.label} (Slab)`, rows: [
          row('Length × Width × Depth', `${el.length} × ${el.width} × ${el.depth} m`),
          row('Soffit Area', `${eb.soffitArea?.toFixed?.(2)} m²`),
          ...eb.formedEdges.map((e) => row('Formed Edge', e.edge)),
          row('Edge Formwork Area', `${eb.edgeArea?.toFixed?.(2)} m²`),
          row('Total Formwork', `${eb.total?.toFixed?.(2)} m²`, { emphasis: true }),
        ] }
      }
      if (el.elementType === 'beam') {
        return { title: `${el.label} (Beam)`, rows: [
          row('Width × Depth × Length', `${el.width} × ${el.depth} × ${el.length} m`),
          row('Total Formwork', `${eb.total?.toFixed?.(2)} m²`, { emphasis: true }),
        ] }
      }
      return { title: `${el.label} (Column, ${el.shape})`, rows: [
        row('Section', el.shape === 'circular' ? `Diameter ${el.diameter} m` : `Width × Depth: ${el.width} × ${el.depth} m`),
        row('Height', `${el.height} m`),
        row('Perimeter', `${eb.perimeter?.toFixed?.(3)} m`),
        row('Per-Instance Formwork', `${eb.perInstance?.toFixed?.(2)} m²`),
        row('Geometry Count', `${eb.geometryCount} No.`),
        row('Total Formwork', `${eb.total?.toFixed?.(2)} m² = ${eb.perInstance?.toFixed?.(2)} × ${eb.geometryCount}`, { emphasis: true }),
      ] }
    })
    const summaryRows = (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit || ''}`.trim() }))
    return { sections, summary: { title: 'Calculation Summary', rows: summaryRows }, selectedBasis: null }
  },
}

MODULE_REGISTRY.excavation = {
  moduleKey: 'excavation', label: 'Excavation / Backfilling', discipline: 'concrete_earthwork', family: 'concrete_earthwork',
  geometrySource: 'area', outputUnit: 'm3', isDedicatedPanel: true,
  explanation: {
    method: 'Excavation: Physical Volume = Plan Area × Depth — Waste % is NEVER applied to this physical volume. '
      + 'Backfilling: Backfill Volume = the selected Excavation Output\'s value minus the selected Concrete Volume Outputs\' values (only outputs the estimator explicitly selects from the same Scope) — never an automatic subtraction of every concrete output.',
    assumptions: [
      'Working space and side slopes are OUT OF SCOPE for this implementation.',
      'Only Saved and Active Resource Quantity Outputs from the same Scope may be selected as Backfill dependencies.',
      'If a selected dependency Output changes after this Backfill was saved, a "Dependency Changed" warning is shown — the saved historical quantity is never silently updated.',
    ],
    inputSources: [
      { label: 'Plan Area, Depth (Excavation)', source: 'Estimator input' },
      { label: 'Excavation source Output, Permanent Volume Outputs (Backfilling)', source: 'Selected from existing Saved Outputs in the same Scope' },
    ],
    glossary: [
      { term: 'Physical Excavation Volume', definition: 'Plan Area × Depth — a real physical volume. Waste % is never applied here because there is no "waste" in excavated ground.' },
      { term: 'Permanent Volume Deduction', definition: 'The volume of permanent works (e.g. foundation concrete) that will occupy part of the excavation and therefore does not need to be backfilled — explicitly selected by the estimator, never assumed.' },
      { term: 'Dependency Changed', definition: 'A warning shown when a selected Excavation or Concrete Volume Output has been recalculated to a different value since this Backfill Output was saved.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    if (breakdownSnapshot?.mode === 'excavation') {
      return {
        sections: [{ title: 'Excavation', rows: [
          row('Plan Area', `${breakdownSnapshot.area} m²`),
          row('Depth', `${breakdownSnapshot.depth} m`),
          row('Physical Excavation Volume (no Waste applied)', `${breakdownSnapshot.physicalVolume?.toFixed?.(3)} m³`, { emphasis: true }),
        ] }],
        summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) },
        selectedBasis: null,
      }
    }
    return {
      sections: [{ title: 'Backfilling', rows: [
        row('Excavation Source', engineeringInputs?.excavationSourceLabel || engineeringInputs?.excavationSourceOutputId),
        row('Excavation Volume Used', `${breakdownSnapshot?.excavationVolume?.toFixed?.(3)} m³`),
        ...(breakdownSnapshot?.permanentVolumes || []).map((p) => row(`Deducted: ${p.label}`, `${p.value?.toFixed?.(3)} m³`)),
        row('Total Permanent Deducted', `${breakdownSnapshot?.totalPermanent?.toFixed?.(3)} m³`),
        row('Final Backfill Volume', `${breakdownSnapshot?.backfillVolume?.toFixed?.(3)} m³`, { emphasis: true }),
      ] }],
      summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) },
      selectedBasis: null,
    }
  },
}

// =======================================================================
// MEP Equipment — Pure Count Module (LOCKED Contract, no multiplier)
// =======================================================================
export function calculateMepEquipment(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one equipment configuration must be defined.')
  const itemBreakdown = []
  let finalTotal = 0
  for (const it of items) {
    if (!it.equipmentType || !it.equipmentType.trim()) throw new Error('Equipment Type is required.')
    if (it.discipline !== 'mechanical' && it.discipline !== 'electrical') throw new Error(`Equipment "${it.equipmentType}": Discipline must be Mechanical or Electrical.`)
    if (typeof it.geometryCount !== 'number' || it.geometryCount <= 0) throw new Error(`Equipment "${it.equipmentType}": Geometry Count must be greater than 0.`)
    if (it.referenceQuantity != null && (typeof it.referenceQuantity !== 'number' || it.referenceQuantity <= 0)) {
      throw new Error(`Equipment "${it.equipmentType}": Reference Quantity, if entered, must be positive.`)
    }
    // LOCKED: Final Quantity = Geometry Count exactly. referenceQuantity/
    // referenceUnit are informational only and NEVER multiply the result.
    const finalQuantity = it.geometryCount
    itemBreakdown.push({ item: it, finalQuantity })
    finalTotal += finalQuantity
  }
  return {
    itemBreakdown,
    steps: [{ label: 'Final Quantity', value: finalTotal, unit: 'No.' }],
    finalQuantity: finalTotal,
    finalUnit: 'no',
  }
}

// =======================================================================
// Pipework — Pure Calculation Function (LOCKED Contract)
// One module, `service` is a descriptive/configuration field only.
// Insulation and Fittings are separate secondary quantities computed
// alongside the main pipe quantity, never merged into it.
// =======================================================================
export function calculatePipework(pipes) {
  if (!Array.isArray(pipes) || pipes.length === 0) throw new Error('At least one pipe configuration must be defined.')
  const pipeBreakdown = []
  let mainTotal = 0, insulationTotal = 0
  const fittingsTotals = {}

  for (const p of pipes) {
    if (typeof p.length !== 'number' || p.length <= 0) throw new Error(`Pipe "${p.label}": length must be positive.`)
    if (typeof p.outerDiameter !== 'number' || p.outerDiameter <= 0) throw new Error(`Pipe "${p.label}": outer diameter must be positive.`)
    if (p.unitBasis !== 'length' && p.unitBasis !== 'weight') throw new Error(`Pipe "${p.label}": unitBasis must be 'length' or 'weight'.`)

    let pipeQuantity, pipeUnit, innerDiameter = null, crossSectionArea = null, weightPerMeter = null
    if (p.unitBasis === 'length') {
      pipeQuantity = p.length
      pipeUnit = 'm'
    } else {
      if (typeof p.wallThickness !== 'number' || p.wallThickness <= 0) throw new Error(`Pipe "${p.label}": wall thickness is required for weight basis.`)
      if (typeof p.materialDensity !== 'number' || p.materialDensity <= 0) throw new Error(`Pipe "${p.label}": material density is required for weight basis.`)
      innerDiameter = p.outerDiameter - 2 * p.wallThickness
      if (innerDiameter <= 0) throw new Error(`Pipe "${p.label}": wall thickness too large — inner diameter is ≤ 0.`)
      crossSectionArea = Math.PI * (Math.pow(p.outerDiameter / 2, 2) - Math.pow(innerDiameter / 2, 2))
      weightPerMeter = crossSectionArea * p.materialDensity
      pipeQuantity = p.length * weightPerMeter
      pipeUnit = 'kg'
    }

    let insulationQuantity = null
    if (p.insulation) {
      const ins = p.insulation
      if (typeof ins.thickness !== 'number' || ins.thickness <= 0) throw new Error(`Pipe "${p.label}" insulation: thickness must be positive.`)
      const insulationOuterDiameter = p.outerDiameter + 2 * ins.thickness
      insulationQuantity = Math.PI * insulationOuterDiameter * p.length // m², LOCKED — never weight-based
      insulationTotal += insulationQuantity
    }

    const fittingsResults = (p.fittings || []).map((f) => {
      if (typeof f.count !== 'number' || f.count <= 0) throw new Error(`Pipe "${p.label}" fitting "${f.type}": count must be positive.`)
      fittingsTotals[f.type] = (fittingsTotals[f.type] || 0) + f.count
      return { type: f.type, count: f.count }
    })

    pipeBreakdown.push({ pipe: p, pipeQuantity, pipeUnit, innerDiameter, crossSectionArea, weightPerMeter, insulationQuantity, fittingsResults })
    mainTotal += pipeQuantity
  }

  return {
    pipeBreakdown,
    steps: [{ label: 'Total Pipe Quantity', value: mainTotal, unit: pipeBreakdown[0]?.pipeUnit || 'm' }],
    finalQuantity: mainTotal,
    finalUnit: pipeBreakdown[0]?.pipeUnit === 'kg' ? 'kg' : 'm',
    secondaryOutputs: {
      insulation: insulationTotal > 0 ? { quantity: insulationTotal, unit: 'm2' } : null,
      fittings: Object.entries(fittingsTotals).map(([type, count]) => ({ type, count })),
    },
  }
}

// =======================================================================
// Containment — Pure Calculation Function (LOCKED Contract)
// Both tray and conduit branches reduce to Final Quantity = Length; the
// distinguishing dimensions (width/height or diameter) are configuration/
// identity fields only and never enter the formula.
// =======================================================================
export function calculateContainment(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one containment configuration must be defined.')
  const itemBreakdown = []
  let mainTotal = 0
  const fittingsTotals = {}

  for (const c of items) {
    if (c.type !== 'tray' && c.type !== 'conduit') throw new Error(`Containment "${c.label}": type must be 'tray' or 'conduit'.`)
    if (typeof c.length !== 'number' || c.length <= 0) throw new Error(`Containment "${c.label}": length must be positive.`)
    if (c.type === 'tray') {
      if (typeof c.width !== 'number' || c.width <= 0 || typeof c.height !== 'number' || c.height <= 0) {
        throw new Error(`Containment "${c.label}": tray width and height are required (configuration only, do not affect quantity).`)
      }
    } else {
      if (typeof c.diameter !== 'number' || c.diameter <= 0) throw new Error(`Containment "${c.label}": conduit diameter is required (configuration only, does not affect quantity).`)
    }
    const fittingsResults = (c.fittings || []).map((f) => {
      if (typeof f.count !== 'number' || f.count <= 0) throw new Error(`Containment "${c.label}" fitting "${f.type}": count must be positive.`)
      fittingsTotals[f.type] = (fittingsTotals[f.type] || 0) + f.count
      return { type: f.type, count: f.count }
    })
    // LOCKED: Final Quantity = Length, regardless of branch.
    itemBreakdown.push({ item: c, finalQuantity: c.length, fittingsResults })
    mainTotal += c.length
  }

  return {
    itemBreakdown,
    steps: [{ label: 'Total Containment Length', value: mainTotal, unit: 'm' }],
    finalQuantity: mainTotal,
    finalUnit: 'm',
    secondaryOutputs: { fittings: Object.entries(fittingsTotals).map(([type, count]) => ({ type, count })) },
  }
}

// =======================================================================
// Ductwork — Pure Calculation Function (LOCKED Contract)
// =======================================================================
export function calculateDuctwork(ducts) {
  if (!Array.isArray(ducts) || ducts.length === 0) throw new Error('At least one duct configuration must be defined.')
  const ductBreakdown = []
  let mainTotal = 0, insulationTotal = 0
  const fittingsTotals = {}

  for (const d of ducts) {
    if (d.shape !== 'rectangular' && d.shape !== 'round') throw new Error(`Duct "${d.label}": shape must be 'rectangular' or 'round'.`)
    if (typeof d.length !== 'number' || d.length <= 0) throw new Error(`Duct "${d.label}": length must be positive.`)
    if (d.unitBasis !== 'area' && d.unitBasis !== 'weight') throw new Error(`Duct "${d.label}": unitBasis must be 'area' or 'weight'.`)

    let perimeter
    if (d.shape === 'rectangular') {
      if (typeof d.width !== 'number' || d.width <= 0 || typeof d.height !== 'number' || d.height <= 0) throw new Error(`Duct "${d.label}": width and height are required.`)
      perimeter = 2 * (d.width + d.height)
    } else {
      if (typeof d.diameter !== 'number' || d.diameter <= 0) throw new Error(`Duct "${d.label}": diameter is required.`)
      perimeter = Math.PI * d.diameter
    }
    const surfaceArea = perimeter * d.length

    let ductQuantity, ductUnit
    if (d.unitBasis === 'area') {
      ductQuantity = surfaceArea
      ductUnit = 'm2'
    } else {
      if (typeof d.sheetThickness !== 'number' || d.sheetThickness <= 0) throw new Error(`Duct "${d.label}": sheet thickness is required for weight basis.`)
      if (typeof d.materialDensity !== 'number' || d.materialDensity <= 0) throw new Error(`Duct "${d.label}": material density is required for weight basis.`)
      ductQuantity = surfaceArea * d.sheetThickness * d.materialDensity
      ductUnit = 'kg'
    }

    let insulationQuantity = null
    if (d.insulation) {
      const ins = d.insulation
      if (typeof ins.thickness !== 'number' || ins.thickness <= 0) throw new Error(`Duct "${d.label}" insulation: thickness must be positive.`)
      const insulatedPerimeter = d.shape === 'rectangular'
        ? 2 * ((d.width + 2 * ins.thickness) + (d.height + 2 * ins.thickness))
        : Math.PI * (d.diameter + 2 * ins.thickness)
      insulationQuantity = insulatedPerimeter * d.length // m², LOCKED — never weight-based
      insulationTotal += insulationQuantity
    }

    const fittingsResults = (d.fittings || []).map((f) => {
      if (typeof f.count !== 'number' || f.count <= 0) throw new Error(`Duct "${d.label}" fitting "${f.type}": count must be positive.`)
      fittingsTotals[f.type] = (fittingsTotals[f.type] || 0) + f.count
      return { type: f.type, count: f.count }
    })

    ductBreakdown.push({ duct: d, perimeter, surfaceArea, ductQuantity, ductUnit, insulationQuantity, fittingsResults })
    mainTotal += ductQuantity
  }

  return {
    ductBreakdown,
    steps: [{ label: 'Total Duct Quantity', value: mainTotal, unit: ductBreakdown[0]?.ductUnit || 'm2' }],
    finalQuantity: mainTotal,
    finalUnit: ductBreakdown[0]?.ductUnit === 'kg' ? 'kg' : 'm2',
    secondaryOutputs: {
      insulation: insulationTotal > 0 ? { quantity: insulationTotal, unit: 'm2' } : null,
      fittings: Object.entries(fittingsTotals).map(([type, count]) => ({ type, count })),
    },
  }
}

// =======================================================================
// Cables — Pure Calculation Function (LOCKED Contract)
// numberOfRuns is the ONLY multiplier. numberOfCores/cableType/cableSize
// are informational/configuration fields and NEVER multiply quantity.
// =======================================================================
export function calculateCables(cables) {
  if (!Array.isArray(cables) || cables.length === 0) throw new Error('At least one cable configuration must be defined.')
  const cableBreakdown = []
  let finalTotal = 0
  for (const c of cables) {
    if (!c.cableType || !c.cableType.trim()) throw new Error('Cable Type is required.')
    if (typeof c.routeLength !== 'number' || c.routeLength <= 0) throw new Error(`Cable "${c.cableType}": route length must be positive.`)
    if (typeof c.numberOfRuns !== 'number' || c.numberOfRuns <= 0) throw new Error(`Cable "${c.cableType}": number of runs must be positive.`)
    if (c.numberOfCores != null && (typeof c.numberOfCores !== 'number' || c.numberOfCores <= 0)) {
      throw new Error(`Cable "${c.cableType}": number of cores, if entered, must be positive.`)
    }
    // LOCKED: Final Quantity = routeLength x numberOfRuns ONLY.
    const finalQuantity = c.routeLength * c.numberOfRuns
    cableBreakdown.push({ cable: c, finalQuantity })
    finalTotal += finalQuantity
  }
  return {
    cableBreakdown,
    steps: [{ label: 'Total Cable Length', value: finalTotal, unit: 'm' }],
    finalQuantity: finalTotal,
    finalUnit: 'm',
  }
}

MODULE_REGISTRY.mep_equipment = {
  moduleKey: 'mep_equipment', label: 'MEP Equipment', discipline: 'mep', family: 'mep',
  geometrySource: 'count', outputUnit: 'no', isDedicatedPanel: true,
  explanation: {
    method: 'Final Quantity = Geometry Count exactly — the frozen number of identical equipment instances this configuration represents. There is no multiplier.',
    assumptions: [
      'Reference Quantity/Unit (e.g. 250 kW, 24 Ways, 500 kVA) are optional descriptive fields stored purely for documentation — they never affect the calculated quantity.',
      'Different equipment types, disciplines, or configurations must be separate takeoff items and separate Outputs.',
      'This module never performs load, sizing, or design calculations — it is a pure quantity count.',
    ],
    inputSources: [
      { label: 'Geometry Count', source: 'Takeoff Geometry (Count measurement)' },
      { label: 'Equipment Type, Discipline, Reference Quantity/Unit', source: 'Estimator input' },
    ],
    glossary: [
      { term: 'Geometry Count', definition: 'The frozen number of identical equipment instances, exactly as recorded from the takeoff at save time.' },
      { term: 'Reference Quantity / Unit', definition: 'An optional descriptive value (e.g. capacity, rating) stored for documentation only — it is never multiplied into the equipment quantity.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: ib.item.equipmentType,
      rows: [
        row('Discipline', ib.item.discipline === 'mechanical' ? 'Mechanical' : 'Electrical'),
        row('Geometry Quantity', `${ib.item.geometryCount} No.`),
        ...(ib.item.referenceQuantity != null ? [row(`Reference (informational only, does not affect quantity)`, `${ib.item.referenceQuantity} ${ib.item.referenceUnit || ''}`)] : []),
        row('Final Quantity', `${ib.finalQuantity} No.`, { emphasis: true }),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.pipework = {
  moduleKey: 'pipework', label: 'Pipework', discipline: 'mep', family: 'mep',
  geometrySource: 'length_or_manual', outputUnit: 'm', isDedicatedPanel: true,
  explanation: {
    method: 'Length basis: Final Quantity = pipe length directly. '
      + 'Weight basis: Inner Diameter = Outer Diameter − 2×Wall Thickness; Cross-Section Area = π×[(OD/2)² − (ID/2)²]; Weight per Metre = Area × Material Density; Final Quantity = Length × Weight per Metre. '
      + 'Insulation is a SEPARATE surface-area quantity: Insulation Outer Diameter = Pipe OD + 2×Insulation Thickness; Insulation Area = π × Insulation OD × Length — it never alters the pipe quantity. '
      + 'Fittings are explicit count groups (e.g. "Elbow 90° — 4 No."), never a percentage allowance.',
    assumptions: [
      'Service (Water/Drainage/Fire/HVAC/Other) is a descriptive/configuration field only — it does not change the formula.',
      'Insulation quantity is always surface area (m²) — never weight-based.',
      'Fittings are recorded as explicit counts per type, never as an implicit percentage multiplier on the pipe quantity.',
    ],
    inputSources: [
      { label: 'Length, Outer Diameter, Wall Thickness, Material Density', source: 'Estimator input' },
      { label: 'Insulation thickness/material, Fittings counts', source: 'Estimator input' },
    ],
    glossary: [
      { term: 'Unit Basis (Length/Weight)', definition: 'Whether this pipe is measured simply by its length (m), or by its steel/material weight (kg) derived from its cross-section.' },
      { term: 'Insulation Quantity', definition: 'A separate surface-area quantity (m²) representing the outer surface of the insulation layer — independent from the pipe\'s own quantity.' },
      { term: 'Fittings', definition: 'Explicit counted items (elbows, tees, reducers, valves) recorded per type — never converted into a percentage allowance on the pipe length/weight.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.pipeBreakdown || []).map((pb) => ({
      title: `${pb.pipe.label} (${pb.pipe.service || 'other'}, ${pb.pipe.material || ''})`,
      rows: [
        row('Length', `${pb.pipe.length} m`),
        row('Outer Diameter', `${pb.pipe.outerDiameter} m`),
        row('Unit Basis', pb.pipe.unitBasis),
        ...(pb.pipe.unitBasis === 'weight' ? [
          row('Wall Thickness', `${pb.pipe.wallThickness} m`),
          row('Inner Diameter', `${pb.innerDiameter?.toFixed?.(4)} m`),
          row('Cross-Section Area', `${pb.crossSectionArea?.toFixed?.(6)} m²`),
          row('Material Density', `${pb.pipe.materialDensity} kg/m³`),
          row('Weight per Metre', `${pb.weightPerMeter?.toFixed?.(3)} kg/m`),
        ] : []),
        row('Pipe Quantity', `${pb.pipeQuantity?.toFixed?.(2)} ${pb.pipeUnit}`, { emphasis: true }),
        ...(pb.insulationQuantity != null ? [row('Insulation Quantity (separate output)', `${pb.insulationQuantity?.toFixed?.(3)} m²`)] : []),
        ...pb.fittingsResults.map((f) => row(`Fitting: ${f.type}`, `${f.count} No.`)),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.containment = {
  moduleKey: 'containment', label: 'Containment (Cable Tray / Conduit)', discipline: 'mep', family: 'mep',
  geometrySource: 'length_or_manual', outputUnit: 'm', isDedicatedPanel: true,
  explanation: {
    method: 'Both Tray and Conduit reduce to the same formula: Final Quantity = Length. Tray width/height and Conduit diameter are configuration-identifying inputs only — they never enter the quantity formula. Fittings are explicit count groups.',
    assumptions: [
      'Tray width/height and conduit diameter distinguish different configurations but do not affect the measured length quantity.',
      'Fittings are recorded as explicit counts per type, never as a percentage allowance.',
    ],
    inputSources: [
      { label: 'Length, Type (Tray/Conduit)', source: 'Estimator input / Takeoff Geometry' },
      { label: 'Tray width/height, or Conduit diameter', source: 'Estimator input (configuration only)' },
    ],
    glossary: [
      { term: 'Tray / Conduit', definition: 'Two containment types sharing the same length-based quantity formula — their dimensions distinguish the configuration but do not multiply the length.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: `${ib.item.label} (${ib.item.type})`,
      rows: [
        row('Length', `${ib.item.length} m`),
        ...(ib.item.type === 'tray' ? [row('Width × Height (configuration only)', `${ib.item.width} × ${ib.item.height} m`)] : [row('Diameter (configuration only)', `${ib.item.diameter} m`)]),
        row('Final Quantity', `${ib.finalQuantity} m`, { emphasis: true }),
        ...ib.fittingsResults.map((f) => row(`Fitting: ${f.type}`, `${f.count} No.`)),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.ductwork = {
  moduleKey: 'ductwork', label: 'Ductwork', discipline: 'mep', family: 'mep',
  geometrySource: 'length_or_manual', outputUnit: 'm2', isDedicatedPanel: true,
  explanation: {
    method: 'Rectangular Perimeter = 2×(Width+Height); Round Perimeter = π×Diameter. Surface Area = Perimeter × Length. '
      + 'Area basis: Final Quantity = Surface Area. Weight basis: Final Quantity = Surface Area × Sheet Thickness × Material Density (default steel density 7850 kg/m³). '
      + 'Insulation is a SEPARATE surface-area quantity using the insulated (enlarged) perimeter — it never alters the duct quantity. '
      + 'Fittings are explicit count groups, never a percentage allowance.',
    assumptions: [
      'Sheet Thickness and Material Density are real engineering inputs required for weight basis — never hidden inside a multiplier.',
      'Insulation quantity is always surface area (m²) — never weight-based.',
      'Fittings are recorded as explicit counts per type, never as an implicit percentage multiplier.',
    ],
    inputSources: [
      { label: 'Shape, Width/Height or Diameter, Length', source: 'Estimator input / Takeoff Geometry' },
      { label: 'Sheet Thickness, Material Density (weight basis)', source: 'Estimator input' },
      { label: 'Insulation thickness, Fittings counts', source: 'Estimator input' },
    ],
    glossary: [
      { term: 'Unit Basis (Area/Weight)', definition: 'Whether this duct is measured by its surface area (m²) or by the actual sheet-metal weight (kg) derived from its thickness and material density.' },
      { term: 'Insulation Quantity', definition: 'A separate surface-area quantity (m²) using the enlarged (insulated) perimeter — independent from the duct\'s own quantity.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.ductBreakdown || []).map((db) => ({
      title: `${db.duct.label} (${db.duct.shape})`,
      rows: [
        row('Dimensions', db.duct.shape === 'rectangular' ? `${db.duct.width} × ${db.duct.height} m` : `Ø${db.duct.diameter} m`),
        row('Length', `${db.duct.length} m`),
        row('Perimeter', `${db.perimeter?.toFixed?.(3)} m`),
        row('Surface Area', `${db.surfaceArea?.toFixed?.(2)} m²`),
        row('Unit Basis', db.duct.unitBasis),
        ...(db.duct.unitBasis === 'weight' ? [row('Sheet Thickness', `${db.duct.sheetThickness} m`), row('Material Density', `${db.duct.materialDensity} kg/m³`)] : []),
        row('Duct Quantity', `${db.ductQuantity?.toFixed?.(2)} ${db.ductUnit}`, { emphasis: true }),
        ...(db.insulationQuantity != null ? [row('Insulation Quantity (separate output)', `${db.insulationQuantity?.toFixed?.(3)} m²`)] : []),
        ...db.fittingsResults.map((f) => row(`Fitting: ${f.type}`, `${f.count} No.`)),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.cables = {
  moduleKey: 'cables', label: 'Cables', discipline: 'mep', family: 'mep',
  geometrySource: 'manual', outputUnit: 'm', isDedicatedPanel: true,
  explanation: {
    method: 'Final Quantity = Route Length × Number of Runs. Number of Runs is the ONLY quantity multiplier — it represents the count of physically separate cable runs following the same route. '
      + 'Number of Cores, Cable Type, and Cable Size are descriptive/configuration fields and are NEVER multiplied into the quantity.',
    assumptions: [
      'Number of Cores describes the internal construction of a single cable — it is informational only and never multiplies the quantity.',
      'Cable Type and Cable Size are descriptive and do not alter the measured route length quantity.',
      'Different cable types/sizes/configurations must be separate Outputs.',
    ],
    inputSources: [
      { label: 'Route Length, Number of Runs', source: 'Estimator input (calculation-affecting)' },
      { label: 'Cable Type, Cable Size, Number of Cores', source: 'Estimator input (informational/configuration only)' },
    ],
    glossary: [
      { term: 'Route Length', definition: 'The physical length of the cable route/path — the primary calculation-affecting geometry.' },
      { term: 'Number of Runs', definition: 'How many physically separate cables follow the same route — the ONLY multiplier applied to Route Length.' },
      { term: 'Number of Cores', definition: 'How many conductors exist inside a single cable — a descriptive property of the cable construction, never a quantity multiplier.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.cableBreakdown || []).map((cb) => ({
      title: `${cb.cable.cableType} (${cb.cable.cableSize || ''})`,
      rows: [
        row('Number of Cores (informational only, does not affect quantity)', cb.cable.numberOfCores ?? '—'),
        row('Route Length', `${cb.cable.routeLength} m`),
        row('Number of Runs', cb.cable.numberOfRuns),
        row('Final Quantity', `${cb.finalQuantity} m = ${cb.cable.routeLength} × ${cb.cable.numberOfRuns}`, { emphasis: true }),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}

// =======================================================================
// Disposal — Pure Calculation Function (LOCKED Contract)
// Depends on frozen Excavation + selected Backfill Outputs. Manual
// Override takes precedence when present. No circular dependency:
// Backfill never references Disposal.
// =======================================================================
export function calculateDisposal(input) {
  if (input.disposalOverride != null) {
    if (typeof input.disposalOverride !== 'number' || input.disposalOverride <= 0) {
      throw new Error('Disposal Override must be a positive number.')
    }
    return {
      overrideApplied: true, disposalOverride: input.disposalOverride,
      excavationVolume: input.excavationVolume ?? null, backfillVolumes: input.backfillVolumes || [],
      computedValue: (input.excavationVolume != null) ? input.excavationVolume - (input.backfillVolumes || []).reduce((s, b) => s + b.value, 0) : null,
      steps: [{ label: 'Manual Override Applied', value: input.disposalOverride, unit: 'm³' }],
      finalQuantity: input.disposalOverride, finalUnit: 'm3',
    }
  }
  if (typeof input.excavationVolume !== 'number' || input.excavationVolume <= 0) throw new Error('Disposal: a valid Excavation source volume is required.')
  const backfillVolumes = input.backfillVolumes || []
  const totalBackfill = backfillVolumes.reduce((s, b) => s + b.value, 0)
  const disposalVolume = input.excavationVolume - totalBackfill
  if (disposalVolume < 0) throw new Error(`Disposal: selected backfill volumes (${totalBackfill.toFixed(2)} m³) exceed the excavation volume (${input.excavationVolume.toFixed(2)} m³).`)
  return {
    overrideApplied: false, excavationVolume: input.excavationVolume, backfillVolumes, totalBackfill, disposalVolume,
    steps: [
      { label: 'Excavation Volume (source)', value: input.excavationVolume, unit: 'm³' },
      { label: 'Selected Backfill Volumes (deducted)', value: totalBackfill, unit: 'm³' },
      { label: 'Final Disposal Volume', value: disposalVolume, unit: 'm³' },
    ],
    finalQuantity: disposalVolume, finalUnit: 'm3',
  }
}

// =======================================================================
// Compaction — Pure Calculation Function (LOCKED Contract)
// Depends EXCLUSIVELY on its own explicit basis/inputs. Backfill may be
// shown as a reference suggestion in the UI but NEVER enters this
// formula. Layers are descriptive/validation detail only — never a
// multiplier.
// =======================================================================
export function calculateCompaction(input) {
  if (input.basis !== 'volume' && input.basis !== 'area') throw new Error('Compaction: basis must be "volume" or "area".')
  let finalQuantity, finalUnit, layerCheck = null

  if (input.basis === 'volume') {
    if (typeof input.volume !== 'number' || input.volume <= 0) throw new Error('Compaction: volume must be positive.')
    finalQuantity = input.volume
    finalUnit = 'm3'
  } else {
    if (typeof input.area !== 'number' || input.area <= 0) throw new Error('Compaction: area must be positive.')
    if (typeof input.depth !== 'number' || input.depth <= 0) throw new Error('Compaction: depth must be positive.')
    finalQuantity = input.area * input.depth
    finalUnit = 'm3'
  }

  if (input.layers && input.layers.length > 0) {
    const layerSum = input.layers.reduce((s, l) => s + (l.thickness || 0), 0)
    const reference = input.basis === 'area' ? input.depth : null
    // Non-blocking, informational only — layers NEVER affect finalQuantity.
    layerCheck = reference != null ? { layerSum, reference, mismatch: Math.abs(layerSum - reference) > 0.001 } : { layerSum, reference: null, mismatch: false }
  }

  return {
    basis: input.basis, layerCheck,
    steps: [{ label: 'Final Compacted Quantity', value: finalQuantity, unit: finalUnit }],
    finalQuantity, finalUnit,
  }
}

// =======================================================================
// Sheet Metal — Pure Calculation Function (LOCKED Contract)
// Ownership boundary vs Ductwork is a documented PROCEDURAL rule, not a
// technical validation — this engine cannot and does not attempt to
// detect physical duplication based on item intent.
// =======================================================================
export function calculateSheetMetal(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one sheet metal item must be defined.')
  const itemBreakdown = []
  let finalTotal = 0
  for (const it of items) {
    if (!it.itemType || !it.itemType.trim()) throw new Error('Item Type is required.')
    if (!['area', 'weight', 'length', 'count'].includes(it.unitBasis)) throw new Error(`Item "${it.itemType}": unitBasis must be area, weight, length, or count.`)
    let finalQuantity, finalUnit
    if (it.unitBasis === 'area') {
      if (typeof it.area !== 'number' || it.area <= 0) throw new Error(`Item "${it.itemType}": area must be positive.`)
      finalQuantity = it.area; finalUnit = 'm2'
    } else if (it.unitBasis === 'weight') {
      if (typeof it.area !== 'number' || it.area <= 0) throw new Error(`Item "${it.itemType}": area is required for weight basis.`)
      if (typeof it.sheetThickness !== 'number' || it.sheetThickness <= 0) throw new Error(`Item "${it.itemType}": sheet thickness is required for weight basis.`)
      if (typeof it.materialDensity !== 'number' || it.materialDensity <= 0) throw new Error(`Item "${it.itemType}": material density is required for weight basis.`)
      finalQuantity = it.area * it.sheetThickness * it.materialDensity; finalUnit = 'kg'
    } else if (it.unitBasis === 'length') {
      if (typeof it.length !== 'number' || it.length <= 0) throw new Error(`Item "${it.itemType}": length must be positive.`)
      finalQuantity = it.length; finalUnit = 'm'
    } else {
      if (typeof it.geometryCount !== 'number' || it.geometryCount <= 0) throw new Error(`Item "${it.itemType}": geometry count must be positive.`)
      finalQuantity = it.geometryCount; finalUnit = 'no'
    }
    itemBreakdown.push({ item: it, finalQuantity, finalUnit })
    finalTotal += finalQuantity
  }
  return { itemBreakdown, steps: [{ label: 'Total Quantity', value: finalTotal, unit: itemBreakdown[0]?.finalUnit || 'm2' }], finalQuantity: finalTotal, finalUnit: itemBreakdown[0]?.finalUnit || 'm2' }
}

// =======================================================================
// Doors & Windows — Pure Calculation Function (LOCKED Contract)
// Count-only in V1. Width/Height are descriptive fields and are
// STRUCTURALLY prevented from entering the formula.
// =======================================================================
export function calculateDoorsWindows(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one door/window item must be defined.')
  const itemBreakdown = []
  let finalTotal = 0
  for (const it of items) {
    if (it.itemType !== 'door' && it.itemType !== 'window') throw new Error('Item type must be "door" or "window".')
    if (typeof it.geometryCount !== 'number' || it.geometryCount <= 0) throw new Error(`${it.itemType}: geometry count must be positive.`)
    // LOCKED: width/height are descriptive only — never read into the formula.
    const finalQuantity = it.geometryCount
    itemBreakdown.push({ item: it, finalQuantity })
    finalTotal += finalQuantity
  }
  return { itemBreakdown, steps: [{ label: 'Total Quantity', value: finalTotal, unit: 'No.' }], finalQuantity: finalTotal, finalUnit: 'no' }
}

// =======================================================================
// Facade — Pure Calculation Function (LOCKED Contract)
// measurementMode is a discriminated union — Area and Panel Count are
// INDEPENDENT outputs and are NEVER multiplied together.
// =======================================================================
export function calculateFacade(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one facade item must be defined.')
  const itemBreakdown = []
  let finalTotal = 0
  let commonUnit = null
  for (const it of items) {
    if (it.measurementMode !== 'area' && it.measurementMode !== 'panel_count') throw new Error('Facade item: measurementMode must be "area" or "panel_count".')
    let finalQuantity, finalUnit
    if (it.measurementMode === 'area') {
      if (typeof it.length !== 'number' || it.length <= 0 || typeof it.height !== 'number' || it.height <= 0) throw new Error(`Facade "${it.label}": length and height must be positive.`)
      finalQuantity = it.length * it.height; finalUnit = 'm2'
    } else {
      if (typeof it.geometryCount !== 'number' || it.geometryCount <= 0) throw new Error(`Facade "${it.label}": geometry count must be positive.`)
      finalQuantity = it.geometryCount; finalUnit = 'no'
    }
    if (commonUnit && commonUnit !== finalUnit) throw new Error('Facade: Area-mode and Panel Count-mode items cannot be combined into the same Output — save them as separate Outputs.')
    commonUnit = finalUnit
    itemBreakdown.push({ item: it, finalQuantity, finalUnit })
    finalTotal += finalQuantity
  }
  return { itemBreakdown, steps: [{ label: 'Total Quantity', value: finalTotal, unit: commonUnit === 'm2' ? 'm²' : 'No.' }], finalQuantity: finalTotal, finalUnit: commonUnit }
}

// =======================================================================
// Miscellaneous Measured Works — Pure Calculation Function (LOCKED)
// Controlled enum-driven framework. Quantity is DIRECT input — no
// internal calculation formula, no Rate/Value field exists here.
// =======================================================================
const MISC_UNIT_MAP = { length: 'm', area: 'm2', volume: 'm3', weight: 'kg', count: 'no' }
export function calculateMiscellaneous(items) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('At least one item must be defined.')
  const itemBreakdown = []
  let finalTotal = 0
  let commonUnit = null
  for (const it of items) {
    if (!it.description || !it.description.trim()) throw new Error('Description is required.')
    if (!Object.keys(MISC_UNIT_MAP).includes(it.measurementType)) throw new Error(`Item "${it.description}": measurementType must be one of ${Object.keys(MISC_UNIT_MAP).join(', ')}.`)
    if (typeof it.quantity !== 'number' || it.quantity <= 0) throw new Error(`Item "${it.description}": quantity must be positive.`)
    const unit = MISC_UNIT_MAP[it.measurementType]
    if (commonUnit && commonUnit !== unit) throw new Error('Miscellaneous: items with different measurementType/unit cannot be combined into the same Output — save them as separate Outputs.')
    commonUnit = unit
    itemBreakdown.push({ item: it, finalQuantity: it.quantity, unit })
    finalTotal += it.quantity
  }
  return { itemBreakdown, steps: [{ label: 'Total Quantity', value: finalTotal, unit: commonUnit }], finalQuantity: finalTotal, finalUnit: commonUnit }
}

MODULE_REGISTRY.disposal = {
  moduleKey: 'disposal', label: 'Disposal', discipline: 'earthwork', family: 'concrete_earthwork',
  geometrySource: 'dependency', outputUnit: 'm3', isDedicatedPanel: true,
  explanation: {
    method: 'Default: Disposal Volume = Excavation Volume − Σ(selected Backfill Outputs\' calculated_value). A Manual Override, when entered, takes precedence over this computed value.',
    assumptions: [
      'Disposal depends on a frozen Excavation Output and explicitly selected Backfill Outputs from the same Scope — never an automatic subtraction of every output.',
      'The Excavation source cannot also appear among the selected Backfill deductions (self-reference is blocked).',
      'Different excavation/disposal locations are a logistical detail, not a calculation input — the physical volume relationship remains as defined.',
    ],
    inputSources: [
      { label: 'Excavation Source, Selected Backfill Outputs', source: 'Selected from existing Saved Outputs in the same Scope' },
      { label: 'Manual Override', source: 'Estimator input (optional, takes precedence when entered)' },
    ],
    glossary: [
      { term: 'Manual Override', definition: 'An explicit disposal quantity entered directly by the estimator — when present, it replaces the computed Excavation-minus-Backfill value entirely.' },
    ],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const rows = breakdownSnapshot?.overrideApplied
      ? [row('Manual Override Applied', `${breakdownSnapshot.disposalOverride} m³`, { emphasis: true }), ...(breakdownSnapshot.computedValue != null ? [row('Computed value (for reference, not used)', `${breakdownSnapshot.computedValue?.toFixed?.(2)} m³`)] : [])]
      : [
          row('Excavation Volume (source)', `${breakdownSnapshot?.excavationVolume?.toFixed?.(2)} m³`),
          ...(breakdownSnapshot?.backfillVolumes || []).map((b) => row(`Deducted: ${b.label}`, `${b.value?.toFixed?.(2)} m³`)),
          row('Final Disposal Volume', `${breakdownSnapshot?.disposalVolume?.toFixed?.(2)} m³`, { emphasis: true }),
        ]
    return { sections: [{ title: 'Disposal', rows }], summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.compaction = {
  moduleKey: 'compaction', label: 'Compaction', discipline: 'earthwork', family: 'concrete_earthwork',
  geometrySource: 'manual', outputUnit: 'm3', isDedicatedPanel: true,
  explanation: {
    method: 'Volume basis: Final Quantity = Volume directly. Area basis: Final Quantity = Area × Depth. Layers are descriptive/validation detail only and NEVER multiply the quantity — Backfill values may be shown as a reference suggestion but never automatically affect this calculation.',
    assumptions: [
      'Compaction depends exclusively on its own explicitly selected basis and inputs — never automatically inherited from a Backfill Output.',
      'Layer thicknesses are checked against the total depth/volume only as an informational, non-blocking cross-check — they are never summed as an independent multiplier.',
    ],
    inputSources: [{ label: 'Volume, or Area and Depth', source: 'Estimator input' }, { label: 'Layers', source: 'Estimator input (descriptive detail only)' }],
    glossary: [{ term: 'Layers', definition: 'An optional descriptive breakdown of the compacted thickness into individual lifts — shown for detail/validation only, never multiplied into the final quantity.' }],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const rows = [row('Basis', breakdownSnapshot?.basis)]
    if (breakdownSnapshot?.layerCheck) {
      rows.push(row('Layer Thicknesses Sum (informational only)', `${breakdownSnapshot.layerCheck.layerSum?.toFixed?.(3)} m${breakdownSnapshot.layerCheck.mismatch ? ' ⚠ does not match depth' : ''}`))
    }
    rows.push(row('Final Quantity', `${breakdownSnapshot?.steps?.[0]?.value?.toFixed?.(2)} m³`, { emphasis: true }))
    return { sections: [{ title: 'Compaction', rows }], summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.sheet_metal = {
  moduleKey: 'sheet_metal', label: 'Sheet Metal', discipline: 'specialist', family: 'specialist',
  geometrySource: 'manual', outputUnit: 'm2', isDedicatedPanel: true,
  explanation: {
    method: 'Area basis: Final Quantity = Area. Weight basis: Final Quantity = Area × Sheet Thickness × Material Density. Length basis: Final Quantity = Length. Count basis: Final Quantity = Geometry Count.',
    assumptions: [
      'OWNERSHIP BOUNDARY (procedural/estimating rule, not a technical validation): Ductwork owns air-distribution sheet-metal elements (ducts, elbows, transitions connected to an air system). This module owns independent, non-air-distribution sheet-metal works (flashing, coping, trims, cladding). The system cannot automatically detect physical duplication based on item intent — this boundary is maintained by the estimator.',
    ],
    inputSources: [{ label: 'Area/Length/Count, Sheet Thickness, Material Density', source: 'Estimator input' }],
    glossary: [{ term: 'Unit Basis', definition: 'The measurement mode for this item — Area (m²), Weight (kg), Length (m), or Count (No.), chosen per item based on how it is actually measured.' }],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: ib.item.itemType,
      rows: [
        row('Unit Basis', ib.item.unitBasis),
        ...(ib.item.unitBasis === 'weight' ? [row('Area', `${ib.item.area} m²`), row('Sheet Thickness', `${ib.item.sheetThickness} m`), row('Material Density', `${ib.item.materialDensity} kg/m³`)] : []),
        row('Final Quantity', `${ib.finalQuantity?.toFixed?.(2)} ${ib.finalUnit}`, { emphasis: true }),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value?.toFixed?.(2)} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.doors_windows = {
  moduleKey: 'doors_windows', label: 'Doors & Windows', discipline: 'specialist', family: 'specialist',
  geometrySource: 'count', outputUnit: 'no', isDedicatedPanel: true,
  explanation: {
    method: 'Final Quantity = Geometry Count exactly. Width and Height are descriptive/reference fields shown for identification only — they never multiply the count.',
    assumptions: ['Area-based measurement is not supported in V1 — Count is the sole measurement basis.', 'Different door/window types must be separate items/Outputs.'],
    inputSources: [{ label: 'Geometry Count', source: 'Estimator input / Takeoff Geometry (count)' }, { label: 'Width, Height', source: 'Estimator input (descriptive only)' }],
    glossary: [{ term: 'Width / Height', definition: 'Descriptive dimensions shown for identification in the Breakdown — they are never multiplied into the quantity, which is Count-only.' }],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: `${ib.item.itemType} (${ib.item.width} × ${ib.item.height} m, descriptive only)`,
      rows: [row('Geometry Count', `${ib.item.geometryCount} No.`), row('Final Quantity', `${ib.finalQuantity} No.`, { emphasis: true })],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.facade = {
  moduleKey: 'facade', label: 'Facade', discipline: 'specialist', family: 'specialist',
  geometrySource: 'manual_or_count', outputUnit: 'm2', isDedicatedPanel: true,
  explanation: {
    method: 'Area mode: Final Quantity = Length × Height. Panel Count mode: Final Quantity = Geometry Count. These are independent measurement modes and are NEVER multiplied together — an Output uses exactly one mode.',
    assumptions: ['Area and Panel Count represent independent contractual measurement items, not a combined formula.', 'Items using different modes cannot be combined into the same Output.'],
    inputSources: [{ label: 'Length, Height (Area mode)', source: 'Estimator input' }, { label: 'Geometry Count (Panel Count mode)', source: 'Estimator input / Takeoff Geometry' }],
    glossary: [{ term: 'Measurement Mode', definition: 'Whether this facade Output measures total surface Area (m²) or Panel Count (No.) — the two are independent and never multiplied.' }],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: ib.item.label,
      rows: [
        row('Measurement Mode', ib.item.measurementMode),
        ...(ib.item.measurementMode === 'area' ? [row('Length × Height', `${ib.item.length} × ${ib.item.height} m`)] : [row('Geometry Count', `${ib.item.geometryCount} No.`)]),
        row('Final Quantity', `${ib.finalQuantity} ${ib.finalUnit === 'm2' ? 'm²' : 'No.'}`, { emphasis: true }),
      ],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}

MODULE_REGISTRY.miscellaneous = {
  moduleKey: 'miscellaneous', label: 'Miscellaneous Measured Works', discipline: 'specialist', family: 'specialist',
  geometrySource: 'manual', outputUnit: 'varies', isDedicatedPanel: true,
  explanation: {
    method: 'Final Quantity = Quantity, entered directly by the estimator. There is no internal calculation formula — this is a controlled, enum-driven direct-entry framework, not a free-text escape hatch.',
    assumptions: ['measurementType is a locked enum (length, area, volume, weight, count) — the unit is derived automatically and cannot be set arbitrarily.', 'No Rate/Value field exists in this module — pricing belongs exclusively to Rate Build-Up.', 'Items with different measurementType cannot be combined into the same Output.'],
    inputSources: [{ label: 'Description, Measurement Type, Quantity', source: 'Estimator input' }],
    glossary: [{ term: 'Measurement Type', definition: 'A locked category (length/area/volume/weight/count) that determines the unit — it cannot be freely typed, preventing arbitrary or inconsistent units.' }],
  },
  renderBreakdown(engineeringInputs, breakdownSnapshot) {
    const sections = (breakdownSnapshot?.itemBreakdown || []).map((ib) => ({
      title: ib.item.description,
      rows: [row('Measurement Type', ib.item.measurementType), row('Final Quantity', `${ib.finalQuantity} ${ib.unit}`, { emphasis: true })],
    }))
    return { sections, summary: { title: 'Calculation Summary', rows: (breakdownSnapshot?.steps || []).map((s) => ({ label: s.label, value: `${s.value} ${s.unit}` })) }, selectedBasis: null }
  },
}
